import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Crosshair, MagnifyingGlass, Sparkle, Package, CheckCircle, Spinner, Tag, Broadcast,
  Cube, Image as ImageIcon, ShieldCheck, ArrowSquareOut, CaretRight, Gear, PaintBrush,
  FilmSlate, Storefront, Info, Circle, Lock, Trash, CaretDown, Translate, ArrowsClockwise,
  Eraser, PencilSimple, Stop,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { API_ORIGIN } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { MakerWorldSessionCard } from "@/components/MakerWorldSessionCard";
import { ClientDiscoveryPanel } from "@/components/ClientDiscoveryPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MediaImage } from "@/creative/components/MediaImage";
import { RetouchEditorModal } from "./product-hunter/RetouchEditorModal";
// MICRO-STEP 3.2 — extracted so the Retouch Editor's "Colore" section can
// import the exact same swatches instead of keeping a second, divergeable
// copy. Values/order unchanged from before.
import { BG_PALETTE } from "./product-hunter/colorPalette";

const SOURCE_ICON = { makerworld: Cube, cgtrader: Cube, other_3d: Cube, web_search: MagnifyingGlass };
const STAGE_ICON = {
  understanding_request: Sparkle, expanding_intent: Sparkle, searching_sources: MagnifyingGlass,
  evaluating_candidates: CheckCircle, checking_duplicates: ShieldCheck, downloading_originals: Package,
  analyzing_product: Cube, preparing_assets: ImageIcon, ranking_results: Sparkle,
};
const DETAIL_TABS = ["originalAssets", "marketingImages", "model3d", "aiAnalysis", "provenance",
  "source", "license", "processing", "etsyListing", "publishing"];
const ETSY_FIELDS = ["title", "description", "tags", "category", "attributes", "price",
  "digitalFiles", "images", "video"];

// 3DArtiX translation `lang_id` -> {code, name} display map, frontend-only (display purposes).
// Mirrors backend/app/product_hunter/etsy_prep.py:_THREEDARTIX_LANGUAGE_CATALOG exactly.
// Source: observed via GET /api/v1/products/{id} on 3DArtiX Live, verified 2026-09-25.
// The repository does not expose a dedicated languages endpoint, so this is an observed
// mapping, not an official versioned catalog. The 3DArtiX CREATE contract forbids a
// `lang_code`/`language`/`locale` field inside a translation object, so the draft itself
// never carries the language name — it must be resolved here for display only.
// Keep in sync with the backend constant if that mapping ever changes.
const THREEDARTIX_LANGUAGE_NAMES = {
  1: { code: "en", name: "English" },
  2: { code: "it", name: "Italiano" },
  3: { code: "es", name: "Español" },
  4: { code: "de", name: "Deutsch" },
  5: { code: "fr", name: "Français" },
  6: { code: "pt", name: "Português" },
  7: { code: "id", name: "Bahasa Indonesia" },
  8: { code: "hi", name: "हिन्दी" },
  9: { code: "bn", name: "বাংলা" },
  10: { code: "ru", name: "Русский" },
  11: { code: "uk", name: "Українська" },
  12: { code: "tr", name: "Türkçe" },
  13: { code: "ar", name: "العربية" },
};

// Resolve a 3DArtiX translation `lang_id` to a short display label. Never invents a name for
// an unknown id: falls back to a neutral "Lang <id>" placeholder instead.
function threedartixLanguageLabel(langId) {
  const entry = THREEDARTIX_LANGUAGE_NAMES[langId];
  if (!entry) return `Lang ${langId}`;
  return `${entry.name} (${entry.code})`;
}

export default function ProductHunter() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ found: 0, saved: 0, processing: 0, ready: 0, published: 0 });
  const [sources, setSources] = useState([]);
  const [stages, setStages] = useState([]);
  const [products, setProducts] = useState([]);
  const [request, setRequest] = useState("");
  const [hunting, setHunting] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [selectedSource, setSelectedSource] = useState("makerworld");
  const [candidates, setCandidates] = useState([]);
  const [stageStates, setStageStates] = useState({});
  const [plan, setPlan] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [acqState, setAcqState] = useState({});
  const [modelState, setModelState] = useState({});
  const [prepState, setPrepState] = useState({});
  const [listing, setListing] = useState(null);
  const [listingOpen, setListingOpen] = useState(false);
  const [listingPid, setListingPid] = useState(null);
  const [listingMarketplace, setListingMarketplace] = useState("etsy");
  // Library-wide destination toggle (Etsy / 3DArtiX). Pure UI state for the current session/action —
  // NEVER persisted on product_hunter_products (a product is not "owned" by a marketplace; only the
  // resulting product_hunter_listings document is, via its own `marketplace` field — V2.1 Phase 1,
  // already backend-marketplace-aware). Controls ONLY which "Prepare"/prompt/listing this page talks
  // to; acquisition, assets and image editing stay completely untouched by this selector.
  const [libraryMarketplace, setLibraryMarketplace] = useState("etsy");
  // SPRINT-PH-14 — Background Editor (image editor category)
  const [bgOpen, setBgOpen] = useState(false);
  const [bgProduct, setBgProduct] = useState(null);
  const [bgAssets, setBgAssets] = useState([]);
  const [bgEditors, setBgEditors] = useState([]);
  const [bgDefault, setBgDefault] = useState(null);
  const [bgEditor, setBgEditor] = useState("default");
  const [bgBg, setBgBg] = useState({ mode: "color", color: "#FFFFFF" });
  const [bgBusy, setBgBusy] = useState("");
  const [bgResults, setBgResults] = useState({});
  const [bgLoading, setBgLoading] = useState(false);
  const [bgError, setBgError] = useState(false);
  const [bgErrors, setBgErrors] = useState({});
  const [bgBatch, setBgBatch] = useState({ running: false, total: 0, done: 0, failed: 0 });
  // Per-card ORIGINALE/RISULTATO source selector: which image "Ritocca immagine",
  // "Rimuovi watermark" and "Modifica tramite ChatGPT Browser" read from. Keyed by the
  // card's own (original) media_asset_id — independent per card, defaults to "original"
  // when absent. Does NOT affect "Sostituisci sfondo"/"Rimuovi sfondo da tutte", which keep
  // using the original exactly as before (not part of this request).
  const [bgSource, setBgSource] = useState({});
  const SOURCE_ORIGINAL = "original";
  const SOURCE_RESULT = "result";
  // MICRO-STEP 1 — Retouch Editor shell ("Ritocca immagine"): open state + which
  // asset it targets. No editing tools yet; onSaved is not wired to bgResults yet.
  const [retouchOpen, setRetouchOpen] = useState(false);
  const [retouchAsset, setRetouchAsset] = useState(null);
  // SPRINT-PH-15 — persistent PH settings + clear catalog
  const [phSettings, setPhSettings] = useState({ max_products_per_search: 12, ai_ranking_enabled: false });
  const [savingSettings, setSavingSettings] = useState(false);
  const [etsyLlm, setEtsyLlm] = useState({ selected: "default", options: [] });
  const [savingEtsyLlm, setSavingEtsyLlm] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const _huntProgress = () => {
    const keys = (stages || []).map((s) => s);
    if (!keys.length) return 0;
    // Once the hunt has settled (no longer polling) the bar is complete.
    if (!hunting && Object.keys(stageStates).length) return 100;
    const done = keys.filter((k) => ["done", "skipped"].includes(stageStates[k])).length;
    return Math.round((done / keys.length) * 100);
  };

  const load = () => {
    api.get("/product-hunter/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/product-hunter/sources").then((r) => setSources(r.data.sources || [])).catch(() => {});
    api.get("/product-hunter/pipeline").then((r) => setStages(r.data.stages || [])).catch(() => {});
    api.get("/product-hunter/products").then((r) => setProducts(r.data.products || [])).catch(() => {});
    api.get("/product-hunter/settings").then((r) => setPhSettings(r.data)).catch(() => {});
    api.get("/product-hunter/etsy-llm-providers").then((r) => setEtsyLlm(r.data)).catch(() => {});
    api.get("/product-hunter/candidates").then((r) => {
      if ((r.data.candidates || []).length) setCandidates(r.data.candidates);
    }).catch(() => {});
  };
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  const huntErrorMessage = (req) => {
    const code = req?.error_code || (req?.error || "").replace("source_error:", "");
    const known = ["cloudflare_challenge", "authentication_required", "endpoint_not_found",
      "forbidden", "rate_limited", "upstream_error", "network_error", "http_error", "blocked"];
    if (known.includes(code)) return t(`ph.sourceErr.${code}`);
    return t("ph.huntFailed", { error: req?.error || "" });
  };

  const runHunt = async () => {
    if (!request.trim()) { toast.error(t("ph.enterRequest")); return; }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setHunting(true);
    setStatusMsg(t("ph.hunting"));
    setStageStates({});
    setCandidates([]);
    setPlan(null);
    try {
      const { data } = await api.post("/product-hunter/hunt", { request, source: selectedSource });
      const smap = {};
      (data.stages || []).forEach((s) => { smap[s.key] = s.status; });
      setStageStates(smap);
      if (data.status === "failed") {
        const m = huntErrorMessage(data);
        setStatusMsg(m);
        toast.error(m);
        setHunting(false);
        return;
      }
      // Background job started — poll the request row for live pipeline progress.
      const requestId = data.request_id;
      pollRef.current = setInterval(async () => {
        try {
          const { data: req } = await api.get(`/product-hunter/requests/${requestId}`);
          const m = {};
          (req.stages || []).forEach((s) => { m[s.key] = s.status; });
          setStageStates(m);
          if (req.plan) setPlan(req.plan);
          if (req.status === "completed" || req.status === "failed") {
            clearInterval(pollRef.current); pollRef.current = null;
            setHunting(false);
            if (req.status === "failed") {
              const m = huntErrorMessage(req);
              setStatusMsg(m);
              toast.error(m);
            } else {
              try {
                const { data: cres } = await api.get(
                  `/product-hunter/candidates?request_id=${requestId}`);
                const found = cres.candidates || [];
                setCandidates(found);
                setStatusMsg(found.length ? t("ph.huntDone", { count: found.length })
                                          : t("ph.noResultsFound"));
              } catch { setStatusMsg(t("ph.noResultsFound")); }
            }
            load();
          }
        } catch {
          clearInterval(pollRef.current); pollRef.current = null;
          setHunting(false);
          setStatusMsg(t("ph.huntError"));
          toast.error(t("ph.huntError"));
        }
      }, 1500);
    } catch (e) {
      setStatusMsg(t("ph.huntError"));
      toast.error(t("ph.huntError"));
      setHunting(false);
    }
  };

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetail(null);
    try {
      const { data } = await api.get(`/product-hunter/candidates/${id}`);
      setDetail(data);
    } catch { setDetail({ error: true }); }
  };

  const saveCandidate = async (id) => {
    try {
      await api.post(`/product-hunter/candidates/${id}/save`);
      toast.success(t("ph.savedForAcq"));
      setCandidates((cs) => cs.map((c) => (c.id === id ? { ...c, status: "saved_for_acquisition" } : c)));
      load();
    } catch { toast.error(t("ph.huntError")); }
  };

  const acquireCandidate = async (id) => {
    setAcqState((s) => ({ ...s, [id]: { status: "processing" } }));
    try {
      const { data } = await api.post(`/product-hunter/candidates/${id}/acquire`);
      const n = (data.original_asset_ids || []).length;
      setAcqState((s) => ({ ...s, [id]: { status: data.status, count: n } }));
      if (data.status === "completed") toast.success(t("ph.acquireCompleted", { count: n }));
      else if (data.status === "partial") toast.success(t("ph.acquirePartial", { count: n }));
      else if (data.status === "blocked") toast.warning(t("ph.acquireBlocked"));
      else toast.error(t("ph.acquireFailed"));
      if (["completed", "partial", "blocked"].includes(data.status)) {
        setCandidates((cs) => cs.map((c) => (c.id === id ? { ...c, status: "acquired" } : c)));
      }
      load();
    } catch {
      setAcqState((s) => ({ ...s, [id]: { status: "error" } }));
      toast.error(t("ph.acquireError"));
    }
  };

  const acquireModelServer = async (candidateId) => {
    setModelState((s) => ({ ...s, [candidateId]: { status: "acquiring" } }));
    try {
      const { data } = await api.post(`/product-hunter/candidates/${candidateId}/acquire-model`);
      setModelState((s) => ({ ...s, [candidateId]: data }));
      const st = data.status;
      if (st === "acquired") toast.success(t("ph.modelAcquired"));
      else if (st === "access_required") toast.warning(t("ph.modelAccessRequired"));
      else if (st === "license_restricted") toast.warning(t("ph.modelLicenseRestricted"));
      else if (st === "blocked") toast.warning(t("ph.modelBlocked"));
      else if (st === "not_available" || st === "unavailable") toast.info(t("ph.modelUnavailable"));
      else toast.error(t("ph.modelFailed"));
      load();
    } catch {
      setModelState((s) => ({ ...s, [candidateId]: { status: "failed" } }));
      toast.error(t("ph.modelFailed"));
    }
  };

  // PH-13: client-side acquisition via the browser extension, with explicit server-side fallback.
  const acquireModelClient = async (candidateId) => {
    setModelState((s) => ({ ...s, [candidateId]: { status: "waiting_extension" } }));
    let requestId;
    try {
      const { data } = await api.post("/product-hunter/client/acquire", { candidate_id: candidateId });
      if (!data.request_id) {
        toast.warning(t("ph.modelClientFallback"));
        return acquireModelServer(candidateId);
      }
      requestId = data.request_id;
    } catch {
      return acquireModelServer(candidateId);
    }
    const started = Date.now();
    const CLIENT_ACQ_TIMEOUT = 180000; // 3 min for large downloads
    const tick = async () => {
      if (Date.now() - started > CLIENT_ACQ_TIMEOUT) {
        toast.warning(t("ph.modelClientTimeout"));
        return acquireModelServer(candidateId);
      }
      try {
        const { data: job } = await api.get(`/product-hunter/client/acquire-jobs/${requestId}`);
        const map = { dispatched: "waiting_extension", claimed: "acquiring", uploading: "uploading" };
        if (job.status === "acquired") {
          setModelState((s) => ({ ...s, [candidateId]: { status: "acquired", model_acquisition: job } }));
          toast.success(t("ph.modelAcquired"));
          load();
          return;
        }
        if (job.status === "failed") {
          const code = job.error_code || "client_error";
          setModelState((s) => ({ ...s, [candidateId]: { status: code } }));
          if (code === "cloudflare_challenge") toast.warning(t("ph.modelCloudflare"));
          else if (code === "captcha_required") toast.warning(t("ph.modelCaptchaRequired"));
          else if (code === "license_required") toast.warning(t("ph.modelLicenseRequired"));
          else if (code === "access_required") toast.warning(t("ph.modelAccessRequired"));
          else toast.error(t("ph.modelClientError"));
          return;
        }
        setModelState((s) => ({ ...s, [candidateId]: { status: map[job.status] || "waiting_extension" } }));
        setTimeout(tick, 2500);
      } catch {
        setTimeout(tick, 2500);
      }
    };
    setTimeout(tick, 2000);
  };

  const acquireModel = async (candidateId) => {
    // Prefer client-side (browser extension) acquisition when an agent is online; otherwise use the
    // existing server-side path. On client timeout/error we explicitly fall back to server-side.
    try {
      const { data } = await api.get("/product-hunter/client/agents");
      if (data.status && data.status.online) return acquireModelClient(candidateId);
    } catch { /* fall through to server-side */ }
    return acquireModelServer(candidateId);
  };

  const saveEtsyLlm = async (provider) => {
    const prev = etsyLlm.selected;
    setSavingEtsyLlm(true);
    setEtsyLlm((s) => ({ ...s, selected: provider }));
    try {
      await api.put("/ai/providers/selected", { provider });
      const { data } = await api.get("/product-hunter/etsy-llm-providers");
      setEtsyLlm(data);
      toast.success(t("ph.etsyLlmSaved"));
    } catch {
      setEtsyLlm((s) => ({ ...s, selected: prev }));
      toast.error(t("ph.etsyLlmSaveError"));
    }
    setSavingEtsyLlm(false);
  };

  // Per-(product, marketplace) key — a product can have an Etsy AND a 3DArtiX prep running/ready at
  // the same time; a single `prepState[id]` would conflate the two statuses when the user switches
  // `libraryMarketplace`. Same map, just keyed more precisely.
  const prepKey = (id, marketplace) => `${id}:${marketplace}`;

  // Stop support: one AbortController per (product, marketplace) preparation in flight, plus a
  // per-key "cancelling" flag so a double click on Stop can never send two cancel requests.
  const prepAbortRef = useRef({});
  const [prepCancelling, setPrepCancelling] = useState({});

  const prepareProduct = async (id, marketplace = libraryMarketplace) => {
    const key = prepKey(id, marketplace);
    const isThreedartix = marketplace === "3dartix";
    if (prepAbortRef.current[key]) return; // already running for this product+marketplace
    const controller = new AbortController();
    prepAbortRef.current[key] = controller;
    setPrepState((s) => ({ ...s, [key]: "processing" }));
    try {
      const endpoint = isThreedartix ? "prepare-3dartix" : "prepare-etsy";
      const { data } = await api.post(`/product-hunter/products/${id}/${endpoint}`, { image_count: 2 },
        { signal: controller.signal });
      if (data.status === "cancelled") {
        // The backend discarded this run (user pressed Stop): never reopen a cancelled preparation.
        setPrepState((s) => ({ ...s, [key]: "cancelled" }));
        load();
        return;
      }
      setPrepState((s) => ({ ...s, [key]: data.status }));
      if (data.status === "ready") {
        toast.success(isThreedartix ? t("ph.threedartixPrepReadyToast") : t("ph.prepReadyToast"));
        openListing(id, marketplace);
      } else if (data.error === "no_acquired_image") {
        toast.warning(t("ph.prepNoImageToast"));
      } else if (data.error === "prompt_not_configured") {
        toast.warning(t("ph.threedartixPromptNotConfigured"));
      } else if (data.error === "draft_contract_invalid") {
        toast.error(data.error_detail
          ? `${t("ph.threedartixContractInvalid")} — ${String(data.error_detail).slice(0, 400)}`
          : t("ph.threedartixContractInvalid"));
      } else if (data.error === "etsy_llm_provider_not_configured" || data.error === "etsy_llm_provider_unavailable") {
        toast.error(t("ph.etsyLlmNotConfigured", { provider: data.provider || "" }));
      } else if (data.error === "chatgpt_browser_unavailable") {
        toast.error(t("ph.chatgptBrowserUnavailable"));
      } else if (data.error === "provider_invalid_json") {
        toast.error(data.error_detail
          ? `${t("ph.providerInvalidJson")} — ${String(data.error_detail).slice(0, 400)}`
          : t("ph.providerInvalidJson"));
      } else if (data.error === "provider_call_failed") {
        toast.error(t("ph.providerCallFailed"));
      } else if (data.error === "all_marketing_images_failed") {
        toast.error(t("ph.imagesFailed", { detail: data.error_detail || "" }));
      } else if (data.error === "image_editor_cannot_generate" || data.error === "image_editor_unavailable") {
        toast.error(t("ph.imageEditorCannotGenerate", { detail: data.error_detail || data.provider || "" }));
      } else {
        toast.error(data.error_detail
          ? `${t("ph.prepFailedToast")} — ${data.error_detail}`
          : t("ph.prepFailedToast"));
      }
      load();
    } catch (err) {
      // Aborted by Stop: the state was already reset by stopPrepare — not a failure, no error toast.
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      setPrepState((s) => ({ ...s, [key]: "failed" }));
      toast.error(t("ph.prepError"));
    } finally {
      if (prepAbortRef.current[key] === controller) delete prepAbortRef.current[key];
    }
  };

  // STOP: first tell the backend to cancel THIS (product, marketplace) preparation (cancels the task,
  // invalidates its ChatGPT Bridge job, marks the listing `cancelled`), then abort the pending HTTP
  // request and release the button. If the backend call fails we keep the preparation state so the
  // user can retry Stop instead of showing a false "cancelled".
  const stopPrepare = async (id, marketplace = libraryMarketplace, regenerating = false) => {
    const key = prepKey(id, marketplace);
    if (prepCancelling[key]) return;
    setPrepCancelling((s) => ({ ...s, [key]: true }));
    try {
      const { data: stopRes } = await api.post(`/product-hunter/products/${id}/prepare-cancel`, { marketplace });
      prepAbortRef.current[key]?.abort();
      delete prepAbortRef.current[key];
      if (regenerating) {
        // Stop during a REGENERATION: the backend keeps the listing `ready` with the previous draft, so
        // drop the local override and let the card follow the backend (never show "cancelled").
        setPrepState((s) => { const n = { ...s }; delete n[key]; return n; });
        if (stopRes?.status === "cancelled") toast.info(t("ph.regenCancelled"));
      } else {
        setPrepState((s) => ({ ...s, [key]: "cancelled" }));
        toast.info(t("ph.prepCancelled"));
      }
      load();
    } catch {
      toast.error(t("ph.prepError"));
    } finally {
      setPrepCancelling((s) => ({ ...s, [key]: false }));
    }
  };

  // Latest listing-dialog view, readable from async handlers (state captured at click time is stale).
  const listingViewRef = useRef({});
  listingViewRef.current = { open: listingOpen, pid: listingPid, marketplace: listingMarketplace };

  // "Regenerate AI draft" (3DArtiX only). Same per-(product, marketplace) key, AbortController and
  // Stop button as the normal preparation — no parallel state system. The backend keeps the previous
  // draft until the new one is fully valid, so every non-success outcome leaves the card on `ready`.
  const regenerateDraft = async (id) => {
    const marketplace = "3dartix";
    const key = prepKey(id, marketplace);
    if (prepAbortRef.current[key]) return; // already running for this product+marketplace
    if (!window.confirm(t("ph.regenConfirm"))) return;
    const controller = new AbortController();
    prepAbortRef.current[key] = controller;
    setPrepState((s) => ({ ...s, [key]: "regenerating" }));
    try {
      const { data } = await api.post(`/product-hunter/products/${id}/regenerate-3dartix`, {},
        { signal: controller.signal });
      if (data.status === "cancelled") {
        // Stop (or a newer run superseded this one): the previous draft is kept — nothing to report.
      } else if (data.status === "already_running") {
        toast.info(t("ph.regenAlreadyRunning"));
      } else if (data.regenerated) {
        toast.success(t("ph.regenDone"));
        const v = listingViewRef.current;
        if (v.open && v.pid === id && v.marketplace === marketplace) openListing(id, marketplace);
      } else if (data.error === "no_draft_to_regenerate") {
        toast.warning(t("ph.regenNoDraft"));
      } else if (data.error === "prompt_not_configured") {
        toast.warning(`${t("ph.threedartixPromptNotConfigured")} — ${t("ph.regenPreviousKept")}`);
      } else if (data.error === "chatgpt_browser_unavailable") {
        toast.error(`${t("ph.chatgptBrowserUnavailable")} — ${t("ph.regenPreviousKept")}`);
      } else {
        toast.error(`${t("ph.regenFailed")} — ${t("ph.regenPreviousKept")}${data.error_detail
          ? `: ${String(data.error_detail).slice(0, 400)}` : ""}`);
      }
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return; // Stop already reset the state
      toast.error(`${t("ph.regenFailed")} — ${t("ph.regenPreviousKept")}`);
    } finally {
      // Only the run that still owns the key clears the override (a newer run must not be clobbered).
      if (prepAbortRef.current[key] === controller) {
        delete prepAbortRef.current[key];
        setPrepState((s) => { const n = { ...s }; delete n[key]; return n; });
        load();
      }
    }
  };

  const openListing = async (id, marketplace = libraryMarketplace) => {
    setListingOpen(true);
    setListing(null);
    setListingPid(id);
    setListingMarketplace(marketplace);
    try {
      const { data } = await api.get(`/product-hunter/products/${id}/listing`, { params: { marketplace } });
      // "Create Etsy draft" / "Delivery ZIP" are Etsy-specific REAL-write/packaging features (see
      // backend/app/product_hunter/etsy_draft.py, delivery.py — both hardcode marketplace="etsy").
      // They must never be fetched/shown for a 3DArtiX review — 3DArtiX stays strictly
      // preparation/review, no real write of any kind.
      let draftRef = null;
      let deliveryRef = null;
      if (marketplace === "etsy") {
        try { draftRef = (await api.get(`/product-hunter/products/${id}/etsy-draft`)).data; } catch { /* none yet */ }
        try { deliveryRef = (await api.get(`/product-hunter/products/${id}/delivery`)).data; } catch { /* none yet */ }
      }
      setListing({ ...data, etsy_draft: draftRef, delivery: deliveryRef });
    } catch { setListing({ error: true }); }
  };

  const prepareDelivery = async (productId) => {
    const { data } = await api.post(`/product-hunter/products/${productId}/prepare-delivery`, {});
    if (data.status === "ready") toast.success(t("ph.deliveryReady"));
    else if (data.error === "no_acquired_model") toast.warning(t("ph.deliveryNoModel"));
    else toast.error(t("ph.deliveryFailed"));
    return data;
  };

  const createEtsyDraft = async (productId, options) => {
    const { data } = await api.post(`/product-hunter/products/${productId}/create-etsy-draft`, options);
    if (data.status === "created") { toast.success(t("ph.etsyDraftCreated")); load(); }
    else if (data.status === "authorization_required") toast.warning(t("ph.etsyAuthRequired"));
    else if (data.error === "taxonomy_required") toast.warning(t("ph.etsyTaxonomyRequired"));
    else if (data.error === "price_required") toast.warning(t("ph.etsyPriceRequired"));
    else toast.error(t("ph.etsyDraftFailed"));
    return data;
  };

  // SPRINT-PH-15 — persistent settings (shared by Hunt + extension) + clear catalog
  const saveSettings = async (patch) => {
    const next = { ...phSettings, ...patch };
    setPhSettings(next);
    setSavingSettings(true);
    try {
      const { data } = await api.put("/product-hunter/settings", patch);
      setPhSettings(data);
      toast.success(t("ph.settingsSaved"));
    } catch { toast.error(t("ph.bgFailed")); }
    setSavingSettings(false);
  };

  const clearCatalog = async () => {
    setClearing(true);
    try {
      const { data } = await api.post("/product-hunter/catalog/clear", { confirm: true });
      const d = data.deleted || {};
      toast.success(`${t("ph.catalogCleared")} (${d.product_hunter_products || 0})`);
      setProducts([]);
      setClearOpen(false);
      load();
    } catch { toast.error(t("ph.bgFailed")); }
    setClearing(false);
  };

  // SPRINT-PH-14 — Background Editor (Image Editor category, local & free)
  const absUrl = (u) => (u ? `${API_ORIGIN}${u}` : null);
  const openBgEditor = async (product) => {
    setBgProduct(product); setBgOpen(true); setBgAssets([]); setBgResults({});
    setBgBg({ mode: "color", color: "#FFFFFF" });
    setBgErrors({}); setBgBatch({ running: false, total: 0, done: 0, failed: 0 });
    setBgSource({});
    setBgError(false); setBgLoading(true);
    Object.keys(cgTimers.current).forEach((k) => { clearTimeout(cgTimers.current[k]); delete cgTimers.current[k]; });
    setCgJobs({}); setCgPrompts({});
    Object.keys(wmTimers.current).forEach((k) => { clearTimeout(wmTimers.current[k]); delete wmTimers.current[k]; });
    setWmJobs({});
    try {
      const [ed, prod] = await Promise.all([
        api.get("/product-hunter/image-editors"),
        api.get(`/product-hunter/products/${product.id}`),
      ]);
      setBgEditors(ed.data.editors || []);
      setBgDefault(ed.data.default_image_editor);
      setBgEditor(ed.data.selected || "default");
      const assets = prod.data.assets || [];
      setBgAssets(assets.filter((a) => a.asset_type === "image" && a.role === "original"));
      const rmap = {};
      // FIX — restore must include manual-retouch derived assets (role="derived_manual_retouch",
      // written by "Ritocca immagine" -> save-retouch-image), not just provider-generated edits
      // (role="derived_edited_image", background replace + ChatGPT accept). Without this, a saved
      // retouch exists in the DB but is silently skipped when the Background Editor reopens, so
      // RISULTATO reverts to whatever edit predates it. Sorted oldest-first by created_at so
      // whichever save happened LAST (of either kind) wins per original — same "last write wins"
      // behavior already used live via setBgResults. Watermark removal
      // (role="derived_watermark_removed") is intentionally still NOT restored here — unchanged.
      assets
        .filter((a) => a.role === "derived_edited_image" || a.role === "derived_manual_retouch")
        .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
        .forEach((d) => {
          if (d.parent_media_asset_id) {
            rmap[d.parent_media_asset_id] = { url: absUrl(d.signed_url), media_asset_id: d.media_asset_id };
          }
        });
      setBgResults(rmap);
      // Reverse lookup (result media_asset_id -> owning card key) built from the SAME rmap
      // above, used only to resolve restored ChatGPT jobs below back to the right card when
      // they were started from a RESULT image instead of the original (see bgSource).
      const resultToCard = {};
      Object.entries(rmap).forEach(([cardKey, r]) => {
        if (r.media_asset_id) resultToCard[r.media_asset_id] = cardKey;
      });
      // Restore ChatGPT edits still open for this product (running or awaiting review), each on ITS
      // OWN card — reuses the Phase 1 endpoint, no new job system.
      try {
        const { data } = await api.get(`/product-hunter/products/${product.id}/chatgpt-edit-image`);
        (data.items || []).forEach((it) => {
          const id = resultToCard[it.source_media_asset_id] || it.source_media_asset_id;
          if (!id) return;
          setCgJob(id, { job_id: it.job_id, state: it.state, error: it.error || null,
                         preview: absUrl(it.preview?.url) });
          setCgPrompts((p) => ({ ...p, [id]: it.prompt || "" }));
          if (it.state === "running") pollCgJob(id, it.job_id, Date.now() + CG_TIMEOUT_MS);
        });
      } catch { /* the editor still works without restorable edits */ }
    } catch { setBgError(true); toast.error(t("ph.bgLoadError")); }
    setBgLoading(false);
  };

  // The Image Editor selection is PERSISTED per org: it drives BOTH this Background Editor and
  // the marketing images generated by "Prepare for Etsy". Text/LLM provider is separate.
  const saveBgEditor = async (provider) => {
    const prev = bgEditor;
    setBgEditor(provider);
    try {
      const { data } = await api.put("/product-hunter/settings", { image_editor_provider: provider });
      setPhSettings(data);
      toast.success(t("ph.bgEditorSaved"));
    } catch {
      setBgEditor(prev);
      toast.error(t("ph.bgEditorSaveError"));
    }
  };

  // Remove a SINGLE item (Hunt candidate or Library product). Unrelated to "Clear product catalog"
  // in Advanced settings, which stays as it is.
  const [removing, setRemoving] = useState({});

  const removeCandidate = async (c) => {
    if (!window.confirm(t("ph.removeConfirm", { name: c.title || t("ph.untitled") }))) return;
    setRemoving((s) => ({ ...s, [c.id]: true }));
    try {
      await api.delete(`/product-hunter/candidates/${c.id}`);
      setCandidates((rows) => rows.filter((x) => x.id !== c.id));
      toast.success(t("ph.removedToast"));
    } catch {
      toast.error(t("ph.removeError"));
    } finally {
      setRemoving((s) => ({ ...s, [c.id]: false }));
    }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(t("ph.removeConfirm", { name: p.title || t("ph.untitled") }))) return;
    setRemoving((s) => ({ ...s, [p.id]: true }));
    try {
      await api.delete(`/product-hunter/products/${p.id}`);
      setProducts((rows) => rows.filter((x) => x.id !== p.id));
      api.get("/product-hunter/stats").then((r) => setStats(r.data)).catch(() => {});
      toast.success(t("ph.removedToast"));
    } catch {
      toast.error(t("ph.removeError"));
    } finally {
      setRemoving((s) => ({ ...s, [p.id]: false }));
    }
  };

  const runBgEdit = async (asset) => {
    setBgBusy(asset.media_asset_id);
    setBgErrors((e) => { const n = { ...e }; delete n[asset.media_asset_id]; return n; });
    try {
      const r = await editOne(asset);
      if (r.ok) {
        setBgResults((m) => ({ ...m, [asset.media_asset_id]: { url: r.url, media_asset_id: r.mediaAssetId } }));
        toast.success(t("ph.bgDone"));
        load();
      } else {
        setBgErrors((e) => ({ ...e, [asset.media_asset_id]: r.error }));
        toast.error(r.error);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || t("ph.bgFailed");
      setBgErrors((er) => ({ ...er, [asset.media_asset_id]: msg }));
      toast.error(msg);
    }
    setBgBusy("");
  };

  // Phase B — one edit call; returns an honest {ok,url|error} without side effects/toasts
  // so it can be reused by both the single-image action and the batch loop.
  const editOne = async (asset) => {
    const { data } = await api.post(`/product-hunter/products/${bgProduct.id}/edit-image`, {
      source_media_asset_id: asset.media_asset_id,
      provider_id: bgEditor,
      background: bgBg,
    });
    if (data.status === "succeeded" && data.url) {
      return { ok: true, url: absUrl(data.url), mediaAssetId: data.media_asset_id };
    }
    return { ok: false, error: data.error || t("ph.bgFailed") };
  };

  // Phase B — batch: remove/replace background on ALL originals sequentially. A per-image
  // failure is recorded and NEVER aborts the run; originals stay immutable (derived assets only).
  const runBgEditAll = async () => {
    if (!bgAssets.length || bgBatch.running) return;
    const total = bgAssets.length;
    setBgBatch({ running: true, total, done: 0, failed: 0 });
    let done = 0, failed = 0;
    for (const asset of bgAssets) {
      setBgBusy(asset.media_asset_id);
      setBgErrors((e) => { const n = { ...e }; delete n[asset.media_asset_id]; return n; });
      try {
        const r = await editOne(asset);
        if (r.ok) {
          setBgResults((m) => ({ ...m, [asset.media_asset_id]: { url: r.url, media_asset_id: r.mediaAssetId } }));
        } else {
          failed += 1;
          setBgErrors((e) => ({ ...e, [asset.media_asset_id]: r.error }));
        }
      } catch (e) {
        failed += 1;
        const msg = e?.response?.data?.detail || t("ph.bgFailed");
        setBgErrors((er) => ({ ...er, [asset.media_asset_id]: msg }));
      }
      done += 1;
      setBgBatch((b) => ({ ...b, done, failed }));
    }
    setBgBusy("");
    setBgBatch((b) => ({ ...b, running: false }));
    load();
    if (failed === 0) toast.success(t("ph.bgBatchDone", { count: done }));
    else toast.warning(t("ph.bgBatchPartial", { done: done - failed, total, failed }));
  };


  // SPRINT-PH-23 Fase 3 — "Modifica tramite ChatGPT Browser" per singola immagine. Nessuna logica
  // di generazione qui: solo gli endpoint della Fase 1. Stato INDIPENDENTE per card, sempre chiuso
  // su media_asset_id, così due modifiche in parallelo non possono incrociare i risultati.
  const [cgPrompts, setCgPrompts] = useState({});
  const [cgJobs, setCgJobs] = useState({});
  const cgTimers = useRef({});

  const CG_POLL_MS = 2000;
  const CG_TIMEOUT_MS = 330000;

  const stopCgPoll = (assetId) => {
    if (cgTimers.current[assetId]) { clearTimeout(cgTimers.current[assetId]); delete cgTimers.current[assetId]; }
  };

  const setCgJob = (assetId, patch) =>
    setCgJobs((m) => ({ ...m, [assetId]: { ...(m[assetId] || {}), ...patch } }));

  const cgErrorMessage = (e) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d?.message) return d.message;
    return t("ph.cgFailed");
  };

  const pollCgJob = (assetId, jobId, deadline) => {
    stopCgPoll(assetId);
    cgTimers.current[assetId] = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/product-hunter/products/${bgProduct.id}/chatgpt-edit-image/${jobId}`);
        // `assetId` is the card key bound by this function's own closure (set once in
        // startCgEdit/openBgEditor's restore loop) — it is ALWAYS correct for this job,
        // regardless of which image (original or a selected result) was actually sent as
        // source_media_asset_id. Do not re-derive it from the job's own echoed field: since
        // the ORIGINALE/RISULTATO selector can send a result's media_asset_id as the source,
        // that field no longer necessarily matches the card key.
        if (data.state === "awaiting_review") {
          setCgJob(assetId, { job_id: jobId, state: "awaiting_review", error: null,
                             preview: absUrl(data.preview?.url) });
          return;
        }
        if (data.state === "failed") {
          setCgJob(assetId, { job_id: jobId, state: "failed", preview: null,
                             error: data.error || t("ph.cgFailed") });
          return;
        }
        if (Date.now() > deadline) {
          setCgJob(assetId, { job_id: jobId, state: "failed", preview: null,
                             error: t("ph.cgTimeout") });
          return;
        }
        pollCgJob(assetId, jobId, deadline);
      } catch (e) {
        setCgJob(assetId, { job_id: jobId, state: "failed", preview: null, error: cgErrorMessage(e) });
      }
    }, CG_POLL_MS);
  };

  // `sourceMediaAssetId` defaults to the card's own id (unchanged behavior for every existing
  // call site). The ORIGINALE/RISULTATO selector passes the currently-selected image's id here
  // instead, WITHOUT changing `id` — which stays the card key used for every piece of local
  // state (cgPrompts/cgJobs) so the UI keeps updating the same card regardless of source.
  const startCgEdit = async (asset, sourceMediaAssetId = asset.media_asset_id) => {
    const id = asset.media_asset_id;
    const prompt = (cgPrompts[id] || "").trim();
    if (!prompt) return;
    setCgJob(id, { state: "running", error: null, preview: null, job_id: null });
    try {
      const { data } = await api.post(
        `/product-hunter/products/${bgProduct.id}/chatgpt-edit-image`,
        { source_media_asset_id: sourceMediaAssetId, prompt });
      setCgJob(id, { job_id: data.job_id, state: "running" });
      pollCgJob(id, data.job_id, Date.now() + CG_TIMEOUT_MS);
    } catch (e) {
      const msg = cgErrorMessage(e);
      setCgJob(id, { state: "failed", error: msg, preview: null });
      toast.error(msg);
    }
  };

  const acceptCgEdit = async (asset) => {
    const id = asset.media_asset_id;
    const job = cgJobs[id];
    if (!job?.job_id) return;
    setCgJob(id, { state: "saving" });
    try {
      const { data } = await api.post(
        `/product-hunter/products/${bgProduct.id}/chatgpt-edit-image/${job.job_id}/accept`);
      setCgJob(id, { state: "accepted", preview: absUrl(data.url) || job.preview, error: null });
      setBgResults((m) => ({ ...m, [id]: { url: absUrl(data.url) || job.preview,
                                           media_asset_id: data.accepted_media_asset_id } }));
      toast.success(t("ph.cgAccepted"));
      load();
    } catch (e) {
      const msg = cgErrorMessage(e);
      setCgJob(id, { state: "awaiting_review", error: msg });
      toast.error(msg);
    }
  };

  const rejectCgEdit = async (asset) => {
    const id = asset.media_asset_id;
    const job = cgJobs[id];
    if (!job?.job_id) return;
    setCgJob(id, { state: "saving" });
    try {
      await api.post(
        `/product-hunter/products/${bgProduct.id}/chatgpt-edit-image/${job.job_id}/reject`);
      stopCgPoll(id);
      setCgJobs((m) => { const n = { ...m }; delete n[id]; return n; });   // card back to original
      toast.success(t("ph.cgRejected"));
    } catch (e) {
      const msg = cgErrorMessage(e);
      setCgJob(id, { state: "awaiting_review", error: msg });
      toast.error(msg);
    }
  };

  // MICRO-STEP FRONTEND — "Rimuovi watermark" per single card, independent of "Sostituisci
  // sfondo" and the ChatGPT Browser block above. Same async CreativeJob contract/polling shape
  // as the ChatGPT block (POST to create+queue, GET to poll), targeted at the two endpoints
  // that already exist for this operation: POST/GET .../watermark-remove-image[/{job_id}].
  // On success the result is written into the SAME bgResults bucket "Sostituisci sfondo"
  // already uses, so it renders in the existing shared RISULTATO panel with no new UI slot.
  // The immutable original (left panel) is never touched by this action.
  const [wmJobs, setWmJobs] = useState({});
  const wmTimers = useRef({});

  const setWmJob = (assetId, patch) =>
    setWmJobs((m) => ({ ...m, [assetId]: { ...(m[assetId] || {}), ...patch } }));

  const stopWmPoll = (assetId) => {
    if (wmTimers.current[assetId]) { clearTimeout(wmTimers.current[assetId]); delete wmTimers.current[assetId]; }
  };

  const wmErrorMessage = (e) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d?.message) return d.message;
    return t("ph.wmFailed");
  };

  const pollWmJob = (assetId, jobId, deadline) => {
    stopWmPoll(assetId);
    wmTimers.current[assetId] = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/product-hunter/products/${bgProduct.id}/watermark-remove-image/${jobId}`);
        if (data.status === "completed") {
          if (data.url) {
            setWmJob(assetId, { job_id: jobId, state: "succeeded", error: null });
            setBgResults((m) => ({ ...m, [assetId]: { url: absUrl(data.url),
                                                       media_asset_id: data.media_asset_id } }));
            toast.success(t("ph.wmDone"));
            load();
          } else {
            setWmJob(assetId, { job_id: jobId, state: "failed", error: data.error || t("ph.wmFailed") });
          }
          return;
        }
        if (data.status === "failed" || data.status === "dead_letter" || data.status === "cancelled") {
          setWmJob(assetId, { job_id: jobId, state: "failed", error: data.error || t("ph.wmFailed") });
          return;
        }
        if (Date.now() > deadline) {
          setWmJob(assetId, { job_id: jobId, state: "failed", error: t("ph.wmTimeout") });
          return;
        }
        pollWmJob(assetId, jobId, deadline);
      } catch (e) {
        setWmJob(assetId, { job_id: jobId, state: "failed", error: wmErrorMessage(e) });
      }
    }, CG_POLL_MS);
  };

  // Same pattern as startCgEdit: `id` (card key, drives wmJobs/wmTimers/bgResults) never
  // changes; `sourceMediaAssetId` (what is actually sent to the backend) defaults to it but
  // can be overridden by the ORIGINALE/RISULTATO selector.
  const startWmRemove = async (asset, sourceMediaAssetId = asset.media_asset_id) => {
    const id = asset.media_asset_id;
    setWmJob(id, { state: "running", error: null, job_id: null });
    try {
      const { data } = await api.post(
        `/product-hunter/products/${bgProduct.id}/watermark-remove-image`,
        { source_media_asset_id: sourceMediaAssetId });
      setWmJob(id, { job_id: data.job_id, state: "running" });
      pollWmJob(id, data.job_id, Date.now() + CG_TIMEOUT_MS);
    } catch (e) {
      const msg = wmErrorMessage(e);
      setWmJob(id, { state: "failed", error: msg });
      toast.error(msg);
    }
  };

  const STAT_META = [
    { key: "found", Icon: MagnifyingGlass }, { key: "saved", Icon: Package },
    { key: "processing", Icon: Spinner }, { key: "ready", Icon: Tag },
    { key: "published", Icon: Broadcast },
  ];

  return (
    <div data-testid="product-hunter-page">
      <PageHeader title={t("ph.title")} subtitle={t("ph.subtitle")}
        action={<Badge variant="secondary" className="rounded-sm gap-1" data-testid="ph-preview-badge">
          <Info size={12} /> {t("ph.previewBadge")}</Badge>} />

      {/* Counters — real org data (zeros until the pipeline runs). */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {STAT_META.map(({ key, Icon }) => (
          <div key={key} className="bg-card border border-border rounded-sm p-5" data-testid={`ph-stat-${key}`}>
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Icon size={16} /><span className="overline text-xs">{t(`ph.stat.${key}`)}</span>
            </div>
            <div className="font-heading text-3xl font-bold">{stats[key] ?? 0}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="hunt">
        <TabsList className="rounded-sm">
          <TabsTrigger value="hunt" className="rounded-sm" data-testid="ph-tab-hunt">
            <Crosshair size={15} className="mr-1" />{t("ph.tabHunt")}
          </TabsTrigger>
          <TabsTrigger value="library" className="rounded-sm" data-testid="ph-tab-library">
            <Package size={15} className="mr-1" />{t("ph.tabLibrary")}
          </TabsTrigger>
        </TabsList>

        {/* ------------------------------------------------------------------ HUNT */}
        <TabsContent value="hunt" className="mt-4 space-y-6">
          {/* Search / Hunt panel */}
          <div className="bg-card border border-border rounded-sm p-6" data-testid="ph-hunt-panel">
            <div className="flex items-center gap-2 mb-3">
              <Sparkle size={18} className="text-primary" weight="fill" />
              <h2 className="font-heading text-lg font-bold">{t("ph.huntPanelTitle")}</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-3 max-w-2xl">{t("ph.huntHelper")}</p>
            <Textarea value={request} onChange={(e) => setRequest(e.target.value)} rows={3}
              placeholder={t("ph.huntPlaceholder")} className="rounded-sm resize-none"
              data-testid="ph-hunt-input" />
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <Button onClick={runHunt} disabled={hunting} className="rounded-sm" data-testid="ph-hunt-button">
                <Crosshair size={16} className="mr-2" />{hunting ? t("ph.hunting") : t("ph.huntButton")}
              </Button>
              <Accordion type="single" collapsible className="border-0">
                <AccordionItem value="adv" className="border-0">
                  <AccordionTrigger className="py-0 text-sm text-muted-foreground hover:no-underline"
                    data-testid="ph-advanced-toggle">
                    <span className="flex items-center gap-1"><Gear size={14} />{t("ph.advanced")}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pt-3">
                    <p className="text-xs text-muted-foreground mb-4">{t("ph.advancedHint")}</p>
                    <div className="space-y-5 max-w-xl" data-testid="ph-advanced-settings">
                      {/* Max products per search (shared by Hunt + extension) */}
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <div className="text-sm font-medium">{t("ph.maxProducts")}</div>
                          <div className="text-[11px] text-muted-foreground">{t("ph.maxProductsHint")}</div>
                        </div>
                        <Input type="number" min={1} max={60} value={phSettings.max_products_per_search}
                          onChange={(e) => setPhSettings((s) => ({ ...s, max_products_per_search: e.target.value }))}
                          onBlur={(e) => {
                            const v = Math.max(1, Math.min(60, parseInt(e.target.value, 10) || 12));
                            saveSettings({ max_products_per_search: v });
                          }}
                          className="w-24 rounded-sm text-center" data-testid="ph-max-products-input" />
                      </div>
                      {/* AI / GPT ranking toggle (search only; content generation unaffected) */}
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <div className="text-sm font-medium">{t("ph.aiRanking")}</div>
                          <div className="text-[11px] text-muted-foreground max-w-md">{t("ph.aiRankingHint")}</div>
                        </div>
                        <Switch checked={!!phSettings.ai_ranking_enabled} disabled={savingSettings}
                          onCheckedChange={(v) => saveSettings({ ai_ranking_enabled: v })}
                          data-testid="ph-ai-ranking-toggle" />
                      </div>
                      {/* Clear catalog */}
                      <div className="flex items-center justify-between gap-4 border-t border-border pt-4">
                        <div>
                          <div className="text-sm font-medium text-destructive">{t("ph.clearCatalog")}</div>
                          <div className="text-[11px] text-muted-foreground max-w-md">{t("ph.clearCatalogHint")}</div>
                        </div>
                        <Button size="sm" variant="outline" className="rounded-sm shrink-0 text-destructive border-destructive/40 hover:bg-destructive/10"
                          onClick={() => setClearOpen(true)} data-testid="ph-clear-catalog-button">
                          <Package size={14} className="mr-1" />{t("ph.clearCatalog")}
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            {statusMsg && (
              <div className="mt-4 flex items-start gap-2 text-sm border border-border bg-secondary/40 rounded-sm p-3"
                data-testid="ph-status-area">
                <Info size={16} className="text-primary mt-0.5 shrink-0" /><span>{statusMsg}</span>
              </div>
            )}
          </div>

          {/* Source selector */}
          <div className="bg-card border border-border rounded-sm p-6" data-testid="ph-sources">
            <div className="flex items-center justify-between mb-1">
              <h3 className="overline text-muted-foreground">{t("ph.sourcesTitle")}</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">{t("ph.sourcesHint")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {sources.map((s) => {
                const Icon = SOURCE_ICON[s.key] || Cube;
                const active = s.status === "available";
                const selected = active && selectedSource === s.key;
                return (
                  <button key={s.key} type="button" disabled={!active}
                    onClick={() => active && setSelectedSource(s.key)}
                    data-testid={`ph-source-${s.key}`}
                    className={`text-left border rounded-sm p-4 transition-colors ${
                      selected ? "border-primary bg-primary/5"
                      : active ? "border-border hover:bg-secondary"
                      : "border-border opacity-60 cursor-not-allowed"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Icon size={20} className={active ? "text-primary" : "text-muted-foreground"} />
                      {active
                        ? <Badge variant="secondary" className="rounded-sm text-[10px]">{t("ph.sourceAvailable")}</Badge>
                        : <Badge variant="outline" className="rounded-sm text-[10px] gap-1"><Lock size={9} />{t("ph.sourcePlanned")}</Badge>}
                    </div>
                    <div className="text-sm font-medium">{s.name}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {active ? t("ph.sourceConfigured") : t("ph.sourceComingSoon")}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* SPRINT-PH-10: MakerWorld Browser Publisher (dedicated, isolated session) */}
          <MakerWorldSessionCard />

          {/* SPRINT-PH-12 (POC): Client-side discovery via browser extension (additive, isolated) */}
          <ClientDiscoveryPanel
            keyword={request}
            onCandidates={(found, msg) => { setCandidates(found); setStatusMsg(msg); load(); }}
            onServerFallback={(kw) => { setRequest(kw); runHunt(); }}
          />

          {/* AI discovery status — pipeline placeholders (NOT executed). */}
          <div className="bg-card border border-border rounded-sm p-6" data-testid="ph-pipeline">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="overline text-muted-foreground">{t("ph.pipelineTitle")}</h3>
              <Badge variant="outline" className="rounded-sm text-[10px]">
                {hunting ? t("ph.hunting")
                  : Object.keys(stageStates).length ? t("ph.pipelineLastRun") : t("ph.pipelineIdle")}
              </Badge>
            </div>
            {(hunting || Object.keys(stageStates).length > 0) && (
              <div className="mb-4" data-testid="ph-hunt-progress">
                <Progress value={_huntProgress()} className="h-1.5" />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-1 gap-y-3">
              {stages.map((st, i) => {
                const Icon = STAGE_ICON[st] || Circle;
                const state = stageStates[st] || "pending";
                const cls = state === "done" ? "border-primary/50 text-primary bg-primary/5"
                  : state === "running" ? "border-amber-500/50 text-amber-500 bg-amber-500/5"
                  : state === "failed" ? "border-destructive/50 text-destructive bg-destructive/5"
                  : "border-border text-muted-foreground bg-secondary/30";
                return (
                  <div key={st} className="flex items-center" data-testid={`ph-stage-${st}`}
                    data-state={state}>
                    <div className={`flex items-center gap-2 border rounded-sm px-3 py-2 ${cls}`}>
                      <Icon size={15} />
                      <span className="text-xs">{t(`ph.stage.${st}`)}</span>
                    </div>
                    {i < stages.length - 1 && <CaretRight size={14} className="text-muted-foreground/50 mx-0.5" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Product results area — real candidates or honest empty state. */}
          <div className="bg-card border border-border rounded-sm p-6" data-testid="ph-results">
            <div className="flex items-center justify-between mb-4">
              <h3 className="overline text-muted-foreground">{t("ph.resultsTitle")}</h3>
              {candidates.length > 0 && (
                <Badge variant="secondary" className="rounded-sm" data-testid="ph-results-count">
                  {t("ph.resultsCount", { count: candidates.length })}</Badge>
              )}
            </div>
            {candidates.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-14" data-testid="ph-results-empty">
                <Crosshair size={40} className="text-muted-foreground/40 mb-3" />
                <p className="text-sm font-medium">{t("ph.noProductsTitle")}</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm">{t("ph.noProductsBody")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ph-candidates-grid">
                {candidates.map((c) => <CandidateCard key={c.id} c={c} t={t}
                  onInspect={() => openDetail(c.id)} onSave={() => saveCandidate(c.id)}
                  onAcquire={() => acquireCandidate(c.id)} acq={acqState[c.id]}
                  onRemove={() => removeCandidate(c)} removing={removing[c.id]}
                  onAcquireModel={() => acquireModel(c.id)} model={modelState[c.id]} />)}
              </div>
            )}
          </div>

          {/* AI / Creative integration entry points — reuse existing systems. */}
          <div className="bg-card border border-border rounded-sm p-6" data-testid="ph-integrations">
            <h3 className="overline text-muted-foreground mb-1">{t("ph.integrationsTitle")}</h3>
            <p className="text-xs text-muted-foreground mb-4">{t("ph.integrationsHint")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Button variant="outline" className="rounded-sm justify-start" onClick={() => navigate("/ai")}
                data-testid="ph-link-ai"><Gear size={16} className="mr-2" />{t("ph.configureAI")}</Button>
              <Button variant="outline" className="rounded-sm justify-start" onClick={() => navigate("/creative/edit")}
                data-testid="ph-link-creative-edit"><PaintBrush size={16} className="mr-2" />{t("ph.editImages")}</Button>
              <Button variant="outline" className="rounded-sm justify-start" onClick={() => navigate("/creative/video")}
                data-testid="ph-link-creative-video"><FilmSlate size={16} className="mr-2" />{t("ph.createVideo")}</Button>
            </div>
          </div>

          {/* Future product-detail workspace structure (disabled preview). */}
          <div className="bg-card border border-border rounded-sm p-6" data-testid="ph-detail-preview">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="overline text-muted-foreground">{t("ph.detailTitle")}</h3>
              <Badge variant="outline" className="rounded-sm text-[10px]">{t("ph.comingSoonBadge")}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-4">{t("ph.detailHint")}</p>
            <div className="flex flex-wrap gap-2">
              {DETAIL_TABS.map((k) => (
                <span key={k} className="text-xs border border-border rounded-sm px-3 py-1.5 text-muted-foreground bg-secondary/30"
                  data-testid={`ph-detail-tab-${k}`}>{t(`ph.detail.${k}`)}</span>
              ))}
            </div>
          </div>

          {/* Future Etsy listing shell (disabled). */}
          <div className="bg-card border border-border rounded-sm p-6" data-testid="ph-etsy-shell">
            <div className="flex items-center gap-2 mb-3">
              <Storefront size={18} className="text-primary" />
              <h3 className="font-heading text-base font-bold">{t("ph.etsyTitle")}</h3>
              <Badge variant="outline" className="rounded-sm text-[10px]" data-testid="ph-etsy-state">{t("ph.etsyNotPrepared")}</Badge>
            </div>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="ph-etsy-states">
              {["notPrepared", "preparing", "ready", "draft", "published"].map((s) => (
                <Badge key={s} variant="secondary" className="rounded-sm text-[10px] opacity-70">{t(`ph.etsyState.${s}`)}</Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mb-3">{t("ph.etsyHint")}</p>
            <div className="flex flex-wrap gap-2">
              {ETSY_FIELDS.map((f) => (
                <span key={f} className="text-xs border border-dashed border-border rounded-sm px-2.5 py-1 text-muted-foreground"
                  data-testid={`ph-etsy-field-${f}`}>{t(`ph.etsyField.${f}`)}</span>
              ))}
            </div>
            <Button variant="outline" disabled className="rounded-sm mt-4" data-testid="ph-etsy-send">
              {t("ph.sendToEtsy")} · {t("ph.comingSoon")}
            </Button>
          </div>
        </TabsContent>

        {/* ------------------------------------------------------------------ LIBRARY */}
        <TabsContent value="library" className="mt-4 space-y-6">
          <div className="bg-card border border-border rounded-sm p-6" data-testid="ph-library">
            <h3 className="overline text-muted-foreground mb-1">{t("ph.libraryTitle")}</h3>
            <p className="text-xs text-muted-foreground mb-4 max-w-3xl">{t("ph.libraryIntro")}</p>

            {/* Multi-marketplace destination toggle — UI-only (see `libraryMarketplace` state comment).
                Controls ONLY which prompt/"Prepare"/listing this page targets; acquisition, assets and
                image editing below stay identical regardless of the selection. */}
            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="ph-library-marketplace-selector">
              <span className="text-xs text-muted-foreground">{t("ph.marketplaceSelectorLabel")}</span>
              <Button type="button" size="sm" className="rounded-sm h-7 text-xs"
                variant={libraryMarketplace === "etsy" ? "default" : "outline"}
                onClick={() => setLibraryMarketplace("etsy")} data-testid="ph-library-marketplace-etsy">
                <Storefront size={13} className="mr-1" />Etsy
              </Button>
              <Button type="button" size="sm" className="rounded-sm h-7 text-xs"
                variant={libraryMarketplace === "3dartix" ? "default" : "outline"}
                onClick={() => setLibraryMarketplace("3dartix")} data-testid="ph-library-marketplace-3dartix">
                <Cube size={13} className="mr-1" />3DArtiX
              </Button>
              <span className="text-[11px] text-muted-foreground">{t("ph.marketplaceSelectorHint")}</span>
            </div>

            {/* Phase D — dedicated LLM selector for "Prepare for Etsy" (per-org, independent of AI Ranking).
                NOT gated by libraryMarketplace: resolve_etsy_provider() is the SAME shared TEXT-provider
                resolution used by prepare() for every marketplace (see backend/app/product_hunter/etsy_prep.py) —
                the "Etsy" naming is legacy, the selection applies to 3DArtiX generation too. */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-5 p-3 border border-border rounded-sm bg-muted/30"
              data-testid="ph-etsy-llm-selector">
              <div className="flex items-center gap-2 min-w-0">
                <Storefront size={15} className="text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium">{t("ph.etsyLlmTitle")}</div>
                  <div className="text-[11px] text-muted-foreground">{t("ph.etsyLlmHint")}</div>
                </div>
              </div>
              <div className="sm:ml-auto shrink-0">
                <Select value={etsyLlm.selected || "default"} disabled={savingEtsyLlm}
                  onValueChange={saveEtsyLlm}>
                  <SelectTrigger className="w-full sm:w-[240px] h-9 rounded-sm text-xs" data-testid="ph-etsy-llm-trigger">
                    <SelectValue placeholder={t("ph.etsyLlmDefault")} />
                  </SelectTrigger>
                  <SelectContent>
                    {(etsyLlm.options || []).map((o) => (
                      <SelectItem key={o.provider_id} value={o.provider_id}
                        disabled={!o.available && o.provider_id !== "default"}
                        data-testid={`ph-etsy-llm-option-${o.provider_id}`}>
                        {o.provider_id === "default" ? t("ph.etsyLlmDefault") : o.name}
                        {!o.available && o.provider_id !== "default" ? ` · ${t("ph.etsyLlmNotSetup")}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {libraryMarketplace === "3dartix"
              ? <ThreeDArtixPromptEditor t={t} />
              : <EtsyPromptEditor t={t} />}

            {products.length === 0 ? (
              <div className="flex flex-col items-center justify-center text-center py-14" data-testid="ph-library-empty">
                <Package size={40} className="text-muted-foreground/40 mb-3" />
                <p className="text-sm font-medium">{t("ph.libraryEmptyTitle")}</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm">{t("ph.libraryEmptyBody")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ph-library-grid">
                {products.map((p) => {
                  const mkt = libraryMarketplace;
                  const isThreedartix = mkt === "3dartix";
                  // Backend status for the CURRENTLY selected destination only (V2.1 UI Phase — see
                  // GET /product-hunter/products: listing_status = etsy, listing_status_3dartix = 3dartix,
                  // two independent product_hunter_listings documents, never conflated).
                  const backendStatus = isThreedartix ? p.listing_status_3dartix : p.listing_status;
                  // A 3DArtiX REGENERATION keeps the listing `processing` + `regenerating` in the DB (its
                  // previous draft is intact); locally it is the "regenerating" override.
                  const backendRegen = isThreedartix && backendStatus === "processing" && !!p.listing_regenerating_3dartix;
                  const ps = prepState[prepKey(p.id, mkt)] || (backendRegen ? "regenerating" : backendStatus);
                  const regenerating = isThreedartix && ps === "regenerating";
                  const preparing = ps === "processing";
                  const ready = ps === "ready";
                  const failed = ps === "failed";
                  const regenError = isThreedartix && ready ? p.listing_regenerate_error_3dartix : null;
                  return (
                  <div key={p.id} className="border border-border rounded-sm p-4 flex flex-col gap-2" data-testid={`ph-product-${p.id}`}>
                    <LibraryThumb images={(p.images || []).map(absUrl)} count={p.image_count} t={t} testid={p.id} />
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium">{p.title}</div>
                      {p.status && (
                        <Badge variant="outline" className="rounded-sm text-[10px] shrink-0"
                          data-testid={`ph-product-status-${p.id}`}>
                          {p.status === "partial" ? t("ph.partial") : p.status === "blocked" ? t("ph.blocked") : p.status === "failed" ? t("ph.failedShort") : t("ph.acquired")}
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] font-mono text-muted-foreground">{p.source}</div>
                    {p.search_request && (
                      <Badge variant="secondary" className="rounded-sm text-[10px] w-fit gap-1" data-testid={`ph-product-session-${p.id}`}>
                        <MagnifyingGlass size={9} />{t("ph.fromSearch")}: {p.search_request}
                      </Badge>
                    )}
                    <div className="text-[11px] text-muted-foreground">
                      {(p.image_count != null ? p.image_count : (p.original_asset_ids || []).length)} {t("ph.assetsAcquired")}
                      {(p.blocked_assets || []).length ? ` · ${(p.blocked_assets).length} ${t("ph.blocked").toLowerCase()}` : ""}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {regenerating ? (
                        <>
                          <Button size="sm" type="button" variant="outline" disabled
                            className="rounded-sm h-7 text-xs" data-testid={`ph-regenerating-${p.id}`}>
                            <Spinner size={12} className="animate-spin mr-1" />{t("ph.regenRunning")}
                          </Button>
                          <Button size="sm" type="button" variant="outline"
                            className="rounded-sm h-7 text-xs ml-auto text-destructive border-destructive/40 hover:bg-destructive/10"
                            onClick={(e) => { e.stopPropagation(); stopPrepare(p.id, mkt, true); }}
                            disabled={!!prepCancelling[prepKey(p.id, mkt)]}
                            data-testid={`ph-stop-${mkt}-${p.id}`}>
                            <Stop size={12} weight="fill" className="mr-1" />{t("ph.stopPrep")}
                          </Button>
                        </>
                      ) : ready ? (
                        <>
                          <Badge variant="secondary" className="rounded-sm text-[10px] gap-1 text-primary" data-testid={`ph-listing-ready-${p.id}`}>
                            <CheckCircle size={11} weight="fill" />{t("ph.readyForReview")}
                          </Badge>
                          <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs ml-auto"
                            onClick={() => openListing(p.id, mkt)} data-testid={`ph-review-draft-${p.id}`}>
                            {isThreedartix ? <Cube size={13} className="mr-1" /> : <Storefront size={13} className="mr-1" />}
                            {t("ph.reviewDraft")}
                          </Button>
                          {isThreedartix && (
                            <Button size="sm" type="button" variant="outline" className="rounded-sm h-7 text-xs"
                              onClick={(e) => { e.stopPropagation(); regenerateDraft(p.id); }}
                              data-testid={`ph-regenerate-${p.id}`}>
                              <ArrowsClockwise size={13} className="mr-1" />{t("ph.regenerateDraft")}
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          {failed && <Badge variant="outline" className="rounded-sm text-[10px] text-destructive border-destructive/40" data-testid={`ph-listing-failed-${p.id}`}>{t("ph.prepareFailed")}</Badge>}
                          <Button size="sm" type="button" variant={preparing ? "outline" : undefined}
                            className={`rounded-sm h-7 text-xs ml-auto${preparing ? " text-destructive border-destructive/40 hover:bg-destructive/10" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (preparing) stopPrepare(p.id, mkt); else prepareProduct(p.id, mkt);
                            }}
                            disabled={!!prepCancelling[prepKey(p.id, mkt)]}
                            data-testid={preparing ? `ph-stop-${mkt}-${p.id}` : `ph-prepare-${mkt}-${p.id}`}>
                            {preparing ? <><Stop size={12} weight="fill" className="mr-1" />{t("ph.stopPrep")}</>
                              : <>{isThreedartix ? <Cube size={13} className="mr-1" /> : <Storefront size={13} className="mr-1" />}
                                {failed ? t("ph.retry") : (isThreedartix ? t("ph.prepare3dartix") : t("ph.prepareEtsy"))}</>}
                          </Button>
                        </>
                      )}
                    </div>
                    {regenError && (
                      <div className="text-[11px] text-destructive" data-testid={`ph-regen-failed-${p.id}`}>
                        {t("ph.regenFailed")} — {t("ph.regenPreviousKept")}
                      </div>
                    )}
                    <Button size="sm" variant="outline"
                      className="rounded-sm h-7 text-xs w-full mt-1"
                      onClick={() => openBgEditor(p)}
                      data-testid={`ph-edit-bg-${p.id}`}>
                      <PaintBrush size={13} className="mr-1" />{t("ph.bgEdit")}
                    </Button>
                    <Button size="sm" variant="outline"
                      className="rounded-sm h-7 text-xs w-full text-destructive border-destructive/40 hover:bg-destructive/10"
                      onClick={() => removeProduct(p)} disabled={removing[p.id]}
                      data-testid={`ph-remove-product-${p.id}`}>
                      {removing[p.id] ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.removing")}</>
                        : <><Trash size={13} className="mr-1" />{t("ph.removeProduct")}</>}
                    </Button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Reuse channels — communicates permanence, no second storage system. */}
          <div className="bg-card border border-border rounded-sm p-6" data-testid="ph-reuse">
            <h3 className="overline text-muted-foreground mb-1">{t("ph.reuseTitle")}</h3>
            <p className="text-xs text-muted-foreground mb-4">{t("ph.reuseHint")}</p>
            <div className="flex flex-wrap gap-2">
              {["etsy", "cgtrader", "marketplaces", "creative", "promotion", "future"].map((k) => (
                <Badge key={k} variant="secondary" className="rounded-sm" data-testid={`ph-reuse-${k}`}>
                  <ArrowSquareOut size={11} className="mr-1" />{t(`ph.reuse.${k}`)}
                </Badge>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Product detail workspace — real discovery data (acquisition/Etsy unavailable). */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-sm" data-testid="ph-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("ph.detailTitle")}</DialogTitle>
            <DialogDescription className="sr-only">{t("ph.detailHint")}</DialogDescription>
          </DialogHeader>
          {!detail ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="ph-detail-loading">
              <Spinner size={22} className="animate-spin mx-auto mb-2" />{t("ph.loading")}
            </div>
          ) : detail.error ? (
            <div className="py-10 text-center text-sm text-destructive">{t("ph.detailError")}</div>
          ) : (
            <div className="space-y-4 text-sm" data-testid="ph-detail-body">
              {detail.detail.preview.images?.[0] && (
                <img src={detail.detail.preview.images[0]} alt="" className="w-full h-48 object-cover rounded-sm border border-border" />
              )}
              <DetailRow label={t("ph.detail.overview")} testid="ph-detail-overview">
                <div className="font-medium">{detail.detail.overview.title || "—"}</div>
                <p className="text-muted-foreground text-xs mt-1">{detail.detail.overview.excerpt || t("ph.noDescription")}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(detail.detail.overview.labels || []).map((l) => (
                    <Badge key={l} variant="outline" className="rounded-sm text-[10px]">{t(`ph.label.${l}`, l)}</Badge>
                  ))}
                </div>
              </DetailRow>
              <DetailRow label={t("ph.detail.source")} testid="ph-detail-source">
                <div className="text-xs">{detail.detail.source.source} · {detail.detail.source.source_id || "—"}</div>
                {detail.detail.source.url && <a href={detail.detail.source.url} target="_blank" rel="noreferrer"
                  className="text-primary text-xs inline-flex items-center gap-1 mt-1"><ArrowSquareOut size={12} />{t("ph.viewSource")}</a>}
              </DetailRow>
              <DetailRow label={t("ph.detail.aiAnalysis")} testid="ph-detail-ai">
                {detail.detail.ai_analysis?.status === "available" ? (
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    {["relevance", "subject_match", "commercial_suitability", "metadata_completeness", "license_confidence"].map((k) => (
                      <div key={k} className="flex justify-between"><span className="text-muted-foreground">{t(`ph.sig.${k}`)}</span><span>{detail.detail.ai_analysis[k]}</span></div>
                    ))}
                  </div>
                ) : <span className="text-xs text-muted-foreground">{t("ph.aiUnavailable")}</span>}
              </DetailRow>
              <DetailRow label={t("ph.detail.license")} testid="ph-detail-license">
                <div className="text-xs">{detail.detail.license_access.license || t("ph.licenseUnavailable")}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{t("ph.accessNote")}</div>
              </DetailRow>
              <DetailRow label={t("ph.detail.provenance")} testid="ph-detail-provenance">
                <div className="text-[11px] font-mono text-muted-foreground break-all">
                  {t("ph.searchRequest")}: {detail.detail.provenance?.search_request || "—"}<br />
                  {t("ph.discoveredAt")}: {detail.detail.provenance?.discovered_at || "—"}
                </div>
              </DetailRow>
              <div className="border border-dashed border-border rounded-sm p-3" data-testid="ph-detail-unavailable">
                <div className="overline text-muted-foreground mb-1">{t("ph.futureSections")}</div>
                <div className="flex flex-wrap gap-1">
                  {["acquisition", "marketing_images", "etsy_listing", "publishing"].map((k) => (
                    <Badge key={k} variant="secondary" className="rounded-sm text-[10px] opacity-70">{t(`ph.future.${k}`)} · {t("ph.comingSoon")}</Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Marketplace listing draft — review-only workspace (nothing sent to Etsy or 3DArtiX by this
          dialog; the only REAL write anywhere in this page is the explicit "Create Etsy Draft" panel
          below, which is itself never rendered for a 3DArtiX review — see ListingBody). */}
      <Dialog open={listingOpen} onOpenChange={setListingOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto rounded-sm" data-testid="ph-listing-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {listingMarketplace === "3dartix" ? <Cube size={18} className="text-primary" /> : <Storefront size={18} className="text-primary" />}
              {listingMarketplace === "3dartix" ? t("ph.threedartixDraftTitle") : t("ph.etsyDraftTitle")}
              <Badge variant="secondary" className="rounded-sm text-[10px] gap-1"><Sparkle size={10} weight="fill" />{t("ph.draftAiBadge")}</Badge>
            </DialogTitle>
            <DialogDescription>{listingMarketplace === "3dartix" ? t("ph.threedartixReviewNote") : t("ph.draftReviewNote")}</DialogDescription>
          </DialogHeader>
          {!listing ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="ph-listing-loading">
              <Spinner size={22} className="animate-spin mx-auto mb-2" />{t("ph.loading")}
            </div>
          ) : listing.error ? (
            <div className="py-10 text-center text-sm text-destructive">{t("ph.detailError")}</div>
          ) : <ListingBody data={listing} t={t} productId={listingPid} marketplace={listingMarketplace}
                onCreateDraft={createEtsyDraft} onPrepareDelivery={prepareDelivery} />}
        </DialogContent>
      </Dialog>

      {/* SPRINT-PH-14 — Background Editor (Image Editor category) */}
      <Dialog open={bgOpen} onOpenChange={setBgOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto rounded-sm" data-testid="ph-bg-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <PaintBrush size={18} className="text-primary" />{t("ph.bgTitle")}
            </DialogTitle>
            <DialogDescription>{t("ph.bgIntro")}</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border border-border rounded-sm p-3" data-testid="ph-bg-controls">
            <div>
              <div className="overline text-muted-foreground mb-1">{t("ph.bgSelectEditor")}</div>
              <select value={bgEditor} onChange={(e) => saveBgEditor(e.target.value)}
                className="w-full rounded-sm border border-border bg-card px-2 py-1.5 text-sm" data-testid="ph-bg-editor-select">
                <option value="default">
                  {t("ph.bgUseDefault")}{bgDefault ? ` (${(bgEditors.find((x) => x.provider_id === bgDefault) || {}).name || bgDefault})` : ""}
                </option>
                {bgEditors.map((e) => (
                  <option key={e.provider_id} value={e.provider_id}>
                    {e.name}{e.supports_generative_edit ? "" : ` · ${t("ph.bgEditorBgOnly")}`}
                  </option>
                ))}
              </select>
              <div className="text-[11px] text-muted-foreground mt-1" data-testid="ph-bg-editor-hint">
                {t("ph.bgEditorSharedHint")}
              </div>
            </div>
            <div>
              <div className="overline text-muted-foreground mb-1">{t("ph.bgBackground")}</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {[
                  { k: "white", mode: "color", color: "#FFFFFF" },
                  { k: "black", mode: "color", color: "#000000" },
                  { k: "transparent", mode: "transparent", color: null },
                ].map((opt) => {
                  const active = bgBg.mode === opt.mode && (opt.mode === "transparent" || bgBg.color?.toUpperCase() === opt.color);
                  return (
                    <button key={opt.k} type="button"
                      onClick={() => setBgBg(opt.mode === "transparent" ? { mode: "transparent" } : { mode: "color", color: opt.color })}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${active ? "border-primary bg-primary/10 text-primary font-medium" : "border-border text-muted-foreground hover:bg-accent"}`}
                      data-testid={`ph-bg-color-${opt.k}`}>
                      {t(`ph.bg${opt.k.charAt(0).toUpperCase() + opt.k.slice(1)}`)}
                    </button>
                  );
                })}
                <input type="color" value={bgBg.mode === "color" ? (bgBg.color || "#FFFFFF") : "#FFFFFF"}
                  onChange={(e) => setBgBg({ mode: "color", color: e.target.value.toUpperCase() })}
                  className="h-7 w-9 rounded-sm border border-border bg-card p-0.5" title={t("ph.bgCustom")}
                  data-testid="ph-bg-color-custom" />
              </div>
              {/* Phase B — full colour palette */}
              <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid="ph-bg-palette">
                {BG_PALETTE.map((hex) => {
                  const active = bgBg.mode === "color" && (bgBg.color || "").toUpperCase() === hex;
                  return (
                    <button key={hex} type="button" title={hex}
                      onClick={() => setBgBg({ mode: "color", color: hex })}
                      className={`h-6 w-6 rounded-full border transition-transform hover:scale-110 ${active ? "ring-2 ring-primary ring-offset-1 ring-offset-background border-primary" : "border-border"}`}
                      style={{ backgroundColor: hex }}
                      data-testid={`ph-bg-swatch-${hex.replace("#", "").toLowerCase()}`} />
                  );
                })}
              </div>
            </div>
          </div>

          {/* Phase B — batch action + live progress */}
          {!bgLoading && !bgError && bgAssets.length > 0 && (
            <div className="flex items-center justify-between gap-3 border border-border rounded-sm p-3 mt-2" data-testid="ph-bg-batch-bar">
              <div className="text-xs text-muted-foreground">{t("ph.bgRunAllHint", { count: bgAssets.length })}</div>
              <Button size="sm" className="rounded-sm h-8 text-xs shrink-0"
                onClick={runBgEditAll} disabled={bgBatch.running || !!bgBusy}
                data-testid="ph-bg-run-all">
                {bgBatch.running
                  ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.bgBatchRunning", { done: bgBatch.done, total: bgBatch.total })}</>
                  : <><PaintBrush size={13} className="mr-1" />{t("ph.bgRunAll")}</>}
              </Button>
            </div>
          )}
          {bgBatch.running && (
            <div className="mt-1" data-testid="ph-bg-batch-progress">
              <Progress value={bgBatch.total ? Math.round((bgBatch.done / bgBatch.total) * 100) : 0} className="h-1.5" />
            </div>
          )}

          {bgLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="ph-bg-loading">
              <Spinner size={22} className="animate-spin mx-auto mb-2" />{t("ph.loading")}
            </div>
          ) : bgError ? (
            <div className="py-10 flex flex-col items-center gap-3 text-center" data-testid="ph-bg-error">
              <Info size={26} className="text-destructive" />
              <p className="text-sm text-muted-foreground">{t("ph.bgLoadError")}</p>
              <Button size="sm" variant="outline" className="rounded-sm"
                onClick={() => openBgEditor(bgProduct)} data-testid="ph-bg-retry">
                {t("ph.retry")}
              </Button>
            </div>
          ) : bgAssets.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="ph-bg-no-images">
              {t("ph.bgNoImages")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2" data-testid="ph-bg-grid">
              {bgAssets.map((a) => {
                const busy = bgBusy === a.media_asset_id;
                const res = bgResults[a.media_asset_id];
                const cg = cgJobs[a.media_asset_id] || {};
                const cgRunning = cg.state === "running" || cg.state === "saving";
                const cgPreview = (cg.state === "awaiting_review" || cg.state === "accepted") ? cg.preview : null;
                const pending = cg.state === "awaiting_review";
                const wm = wmJobs[a.media_asset_id] || {};
                const wmRunning = wm.state === "running";
                const resultUrl = cgPreview || (res ? res.url : null);
                // ORIGINALE/RISULTATO source selector for THIS card only. A result is
                // selectable only once it actually carries a media_asset_id (some restored/
                // legacy results may not) — otherwise the selector silently behaves as if
                // ORIGINALE were chosen, which is the required default.
                const canUseResultAsSource = !!(res && res.media_asset_id);
                const selectedSource = (bgSource[a.media_asset_id] === SOURCE_RESULT && canUseResultAsSource)
                  ? SOURCE_RESULT : SOURCE_ORIGINAL;
                const effectiveMediaAssetId = selectedSource === SOURCE_RESULT
                  ? res.media_asset_id : a.media_asset_id;
                const effectiveImageUrl = selectedSource === SOURCE_RESULT
                  ? res.url : absUrl(a.signed_url);
                return (
                  <div key={a.media_asset_id} className="border border-border rounded-sm p-3 space-y-2" data-testid={`ph-bg-item-${a.media_asset_id}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="overline text-muted-foreground mb-1">{t("ph.bgOriginal")}</div>
                        <MediaImage url={absUrl(a.signed_url)} alt="original"
                          className="w-full h-32 object-contain rounded-sm border border-border bg-muted/30" />
                      </div>
                      <div>
                        <div className="overline text-muted-foreground mb-1 flex items-center gap-1.5">
                          {t("ph.bgResult")}
                          {pending && (
                            <Badge variant="outline" className="rounded-sm text-[9px] px-1 py-0 border-primary text-primary"
                              data-testid={`ph-cg-pending-badge-${a.media_asset_id}`}>
                              {t("ph.cgUnsaved")}
                            </Badge>
                          )}
                        </div>
                        <div className={`w-full h-32 rounded-sm border flex items-center justify-center ${pending ? "border-primary" : "border-border"} ${resultUrl ? "bg-[repeating-conic-gradient(#e5e5e5_0%_25%,#fff_0%_50%)] bg-[length:16px_16px]" : "bg-muted/30"}`}>
                          {resultUrl ? (
                            <span className="block w-full h-32" data-testid={cgPreview ? `ph-cg-result-${a.media_asset_id}` : `ph-bg-result-${a.media_asset_id}`}>
                              <MediaImage url={resultUrl} alt="edited" className="w-full h-32 object-contain" />
                            </span>
                          ) : busy || cgRunning || wmRunning ? (
                            <Spinner size={20} className="animate-spin text-muted-foreground" />
                          ) : bgErrors[a.media_asset_id] ? (
                            <span className="flex flex-col items-center gap-1 text-destructive text-center px-2"
                              data-testid={`ph-bg-item-error-${a.media_asset_id}`}>
                              <Info size={20} />
                              <span className="text-[10px]">{t("ph.bgItemError")}</span>
                            </span>
                          ) : (
                            <span className="flex flex-col items-center gap-1 text-muted-foreground/50">
                              <ImageIcon size={22} />
                              <span className="text-[10px]">{t("ph.bgResult")}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ORIGINALE/RISULTATO source selector — governs "Ritocca immagine",
                        "Rimuovi watermark" and "Modifica tramite ChatGPT Browser" below.
                        Independent per card; does NOT affect "Sostituisci sfondo"/"Rimuovi
                        sfondo da tutte", which keep using the original as before. Defaults to
                        ORIGINALE when nothing is selected or no result exists yet. */}
                    <div className="flex items-center gap-1.5" data-testid={`ph-source-select-${a.media_asset_id}`}>
                      <span className="text-[10px] text-muted-foreground">{t("ph.sourceLabel")}:</span>
                      <Button type="button" size="sm"
                        variant={selectedSource === SOURCE_ORIGINAL ? "default" : "outline"}
                        className="rounded-sm h-6 text-[11px] px-2"
                        onClick={() => setBgSource((m) => ({ ...m, [a.media_asset_id]: SOURCE_ORIGINAL }))}
                        data-testid={`ph-source-original-${a.media_asset_id}`}>
                        {t("ph.bgOriginal")}
                      </Button>
                      <Button type="button" size="sm"
                        variant={selectedSource === SOURCE_RESULT ? "default" : "outline"}
                        className="rounded-sm h-6 text-[11px] px-2"
                        disabled={!canUseResultAsSource}
                        title={!canUseResultAsSource ? t("ph.sourceHint") : undefined}
                        onClick={() => setBgSource((m) => ({ ...m, [a.media_asset_id]: SOURCE_RESULT }))}
                        data-testid={`ph-source-result-${a.media_asset_id}`}>
                        {t("ph.bgResult")}
                      </Button>
                    </div>

                    <Button size="sm" className="rounded-sm h-7 text-xs w-full"
                      onClick={() => runBgEdit(a)} disabled={busy || bgBatch.running}
                      data-testid={`ph-bg-run-${a.media_asset_id}`}>
                      {busy ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.bgProcessing")}</>
                        : <><PaintBrush size={13} className="mr-1" />{t("ph.bgRun")}</>}
                    </Button>

                    {/* MICRO-STEP FRONTEND — Rimuovi watermark: independent per-card action, own
                        async job/polling, writes into the same shared RISULTATO panel above. */}
                    <div className="space-y-1" data-testid={`ph-wm-block-${a.media_asset_id}`}>
                      <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs w-full"
                        onClick={() => startWmRemove(a, effectiveMediaAssetId)}
                        disabled={wmRunning || busy || cgRunning || pending || bgBatch.running}
                        data-testid={`ph-wm-run-${a.media_asset_id}`}>
                        {wmRunning
                          ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.wmProcessing")}</>
                          : <><Eraser size={13} className="mr-1" />{t("ph.wmRun")}</>}
                      </Button>
                      {wm.state === "failed" && (
                        <div className="flex items-center justify-between gap-2 text-[11px] text-destructive"
                          data-testid={`ph-wm-error-${a.media_asset_id}`}>
                          <span className="break-words">{wm.error}</span>
                          <Button size="sm" variant="outline" className="rounded-sm h-6 text-[11px] shrink-0"
                            onClick={() => startWmRemove(a, effectiveMediaAssetId)} data-testid={`ph-wm-retry-${a.media_asset_id}`}>
                            {t("ph.retry")}
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* MICRO-STEP 1 — "Ritocca immagine": manual retouch editor shell (view-only for
                        now, no tools yet). Independent action; does not touch bgResults yet. */}
                    <div className="border-t border-dashed border-border pt-2"
                      data-testid={`ph-retouch-block-${a.media_asset_id}`}>
                      <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs w-full"
                        onClick={() => {
                          // `cardKey` (always the ORIGINAL's id) is what onSaved below writes
                          // back into bgResults, so the retouched result always lands in THIS
                          // card's RISULTATO panel — regardless of whether the editor itself
                          // was opened on the original or on the current result image.
                          setRetouchAsset({ media_asset_id: effectiveMediaAssetId,
                                           cardKey: a.media_asset_id, url: effectiveImageUrl });
                          setRetouchOpen(true);
                        }}
                        disabled={bgBatch.running}
                        data-testid={`ph-retouch-open-${a.media_asset_id}`}>
                        <PencilSimple size={13} className="mr-1" />{t("ph.retouchRun")}
                      </Button>
                    </div>

                    {/* SPRINT-PH-23 Fase 3 — edit via the user's own ChatGPT session (separate action) */}
                    <div className="border-t border-dashed border-border pt-2 space-y-1.5"
                      data-testid={`ph-cg-block-${a.media_asset_id}`}>
                      <div className="overline text-muted-foreground">{t("ph.cgLabel")}</div>
                      <Textarea rows={2} value={cgPrompts[a.media_asset_id] || ""}
                        onChange={(e) => setCgPrompts((p) => ({ ...p, [a.media_asset_id]: e.target.value }))}
                        placeholder={t("ph.cgPlaceholder")}
                        className="rounded-sm text-xs min-h-[52px]"
                        disabled={cgRunning || pending}
                        data-testid={`ph-cg-prompt-${a.media_asset_id}`} />
                      <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs w-full"
                        onClick={() => startCgEdit(a, effectiveMediaAssetId)}
                        disabled={cgRunning || pending || bgBatch.running || !(cgPrompts[a.media_asset_id] || "").trim()}
                        data-testid={`ph-cg-run-${a.media_asset_id}`}>
                        {cgRunning
                          ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.cgWaiting")}</>
                          : <><Sparkle size={13} className="mr-1" />{t("ph.cgRun")}</>}
                      </Button>
                      {cg.state === "running" && (
                        <div className="text-[11px] text-muted-foreground" data-testid={`ph-cg-status-${a.media_asset_id}`}>
                          {t("ph.cgWaitingHint")}
                        </div>
                      )}
                      {pending && (
                        <div className="flex items-center gap-1.5" data-testid={`ph-cg-review-${a.media_asset_id}`}>
                          <Button size="sm" className="rounded-sm h-7 text-xs flex-1"
                            onClick={() => acceptCgEdit(a)} data-testid={`ph-cg-accept-${a.media_asset_id}`}>
                            <CheckCircle size={13} className="mr-1" />{t("ph.cgAccept")}
                          </Button>
                          <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs flex-1"
                            onClick={() => rejectCgEdit(a)} data-testid={`ph-cg-reject-${a.media_asset_id}`}>
                            <Trash size={13} className="mr-1" />{t("ph.cgReject")}
                          </Button>
                        </div>
                      )}
                      {cg.state === "accepted" && (
                        <div className="text-[11px] text-primary" data-testid={`ph-cg-saved-${a.media_asset_id}`}>
                          {t("ph.cgAccepted")}
                        </div>
                      )}
                      {cg.error && (
                        <div className="text-[11px] text-destructive break-words"
                          data-testid={`ph-cg-error-${a.media_asset_id}`}>
                          {cg.error}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* MICRO-STEP 1 (+3.8) — Retouch Editor: manual mask/brush/eraser/remove-area/fill
          editing, entirely local until "Salva" uploads the result via the existing Media
          Engine and links it as a new derived asset (backend save-retouch-image). onSaved
          fires only once that round-trip has genuinely succeeded, mirroring runBgEdit's
          exact bgResults contract so the result shows up in the same RISULTATO panel as
          "Sostituisci sfondo"/watermark/ChatGPT Browser. */}
      <RetouchEditorModal
        open={retouchOpen}
        onOpenChange={(v) => { setRetouchOpen(v); if (!v) setRetouchAsset(null); }}
        imageUrl={retouchAsset ? retouchAsset.url : null}
        mediaAssetId={retouchAsset?.media_asset_id}
        productId={bgProduct?.id}
        onSaved={(url, savedMediaAssetId) => {
          if (retouchAsset) {
            // Always write back to `cardKey` (the card's ORIGINAL id), never to
            // `retouchAsset.media_asset_id` — the latter is whichever image (original or a
            // selected result) was used as the editor's SOURCE, which may differ from the
            // card when the ORIGINALE/RISULTATO selector was set to "Risultato". Writing to
            // `cardKey` is what makes the retouched image land in THIS card's RISULTATO panel.
            setBgResults((m) => ({ ...m, [retouchAsset.cardKey]: { url: absUrl(url),
                                                                   media_asset_id: savedMediaAssetId } }));
            toast.success(t("ph.retouchSaved"));
            load();
          }
        }}
      />

      {/* SPRINT-PH-15 — Clear catalog confirmation */}
      <AlertDialog open={clearOpen} onOpenChange={setClearOpen}>
        <AlertDialogContent data-testid="ph-clear-catalog-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("ph.clearConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("ph.clearConfirmBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ph-clear-catalog-cancel">{t("ph.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); clearCatalog(); }}
              disabled={clearing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="ph-clear-catalog-confirm">
              {clearing ? t("ph.clearing") : t("ph.clearConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// SPRINT-PH-15 — Library thumbnail with a light carousel (reuses acquired images; no re-download).
function LibraryThumb({ images, count, t, testid }) {
  const [idx, setIdx] = useState(0);
  const list = images || [];
  if (list.length === 0) {
    return (
      <div className="w-full h-40 rounded-sm border border-border bg-muted/30 flex items-center justify-center text-muted-foreground/50"
        data-testid={`ph-product-thumb-empty-${testid}`}>
        <ImageIcon size={26} />
      </div>
    );
  }
  const cur = ((idx % list.length) + list.length) % list.length;
  return (
    <div className="relative w-full h-40 rounded-sm border border-border overflow-hidden bg-muted/20 group"
      data-testid={`ph-product-thumb-${testid}`}>
      <MediaImage url={list[cur]} alt="product" className="w-full h-40 object-contain" />
      {list.length > 1 && (
        <>
          <button type="button" onClick={() => setIdx(cur - 1)}
            className="absolute left-1 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-background/80 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`ph-product-thumb-prev-${testid}`}>
            <CaretRight size={12} className="rotate-180" />
          </button>
          <button type="button" onClick={() => setIdx(cur + 1)}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-background/80 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`ph-product-thumb-next-${testid}`}>
            <CaretRight size={12} />
          </button>
          <Badge variant="secondary" className="absolute top-1 right-1 rounded-sm text-[10px] gap-1"
            data-testid={`ph-product-image-count-${testid}`}>
            <ImageIcon size={9} />{count ?? list.length} {t("ph.images")}
          </Badge>
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
            {list.map((_, i) => (
              <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === cur ? "bg-primary" : "bg-background/70 border border-border"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ListingBody({ data, t, productId, marketplace = "etsy", onCreateDraft, onPrepareDelivery }) {
  const backend = API_ORIGIN;
  const abs = (u) => (u ? `${backend}${u}` : null);
  const d = data.listing?.draft || {};
  const listing = data.listing || {};
  const images = data.marketing_images || [];
  const isThreedartix = marketplace === "3dartix";
  return (
    <div className="space-y-4 text-sm" data-testid="ph-listing-body">
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <Badge variant="outline" className="rounded-sm gap-1"><ShieldCheck size={11} />{t("ph.draftGrounded")}</Badge>
        {/* `ai_confidence` comes from the draft's own `confidence` (backend `_persist`, default 0). The
            3DArtiX CREATE contract has no such field, so its "0" was a misleading default — hidden. */}
        {!isThreedartix && (
          <Badge variant="outline" className="rounded-sm">{t("ph.draftConfidence")}: {listing.ai_confidence ?? 0}</Badge>
        )}
        {listing.provenance?.license && <Badge variant="outline" className="rounded-sm">{t("ph.draftLicense")}: {listing.provenance.license}</Badge>}
      </div>

      {/* Marketing/original images — SAME shared asset pipeline regardless of marketplace (the
          backend never duplicates assets per destination; see product_hunter_assets). */}
      {(images.length > 0 || data.original_image_url) && (
        <div data-testid="ph-listing-images">
          <div className="overline text-muted-foreground mb-2">{t("ph.draftMarketingImages")}</div>
          <div className="grid grid-cols-3 gap-2">
            {data.original_image_url && (
              <div className="relative">
                <ImgTile src={abs(data.original_image_url)} t={t} />
                <span className="absolute bottom-1 left-1 text-[9px] bg-background/80 border border-border rounded-sm px-1">{t("ph.draftOriginal")}</span>
              </div>
            )}
            {images.map((im, i) => (
              <div key={im.media_asset_id} data-testid={`ph-listing-image-${i}`}>
                <ImgTile src={abs(im.url)} t={t} />
              </div>
            ))}
          </div>
        </div>
      )}

      {isThreedartix ? (
        // 3DArtiX draft shape (backend/app/ecommerce/threedartix_contract.py) is STRUCTURALLY
        // different from the Etsy draft — never rendered through the Etsy fields below, which would
        // show blanks/mismatched data (e.g. no top-level `title`, it lives in `translations[0]`).
        <>
          <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-3 text-[11px] text-amber-700"
            data-testid="ph-listing-3dartix-note">
            {t("ph.threedartixReviewNote")}
          </div>
          <ListingField label={t("ph.draftTitleField")} testid="ph-listing-title">
            <div className="font-medium">{d.translations?.[0]?.title || "—"}</div>
          </ListingField>
          <ListingField label={t("ph.draftDescription")} testid="ph-listing-description">
            <p className="text-xs text-muted-foreground whitespace-pre-wrap">{d.translations?.[0]?.description || "—"}</p>
          </ListingField>
          {(d.translations || []).length > 1 && (
            <ListingField label={t("ph.draftAdditionalLanguages")} testid="ph-listing-3dartix-languages">
              <div className="flex flex-wrap gap-1">
                {d.translations.slice(1).map((tr) => (
                  <Badge key={tr.lang_id} variant="outline" className="rounded-sm text-[10px]">
                    {threedartixLanguageLabel(tr.lang_id)}
                  </Badge>
                ))}
              </div>
            </ListingField>
          )}
          <ListingField label={t("ph.draftTags")} testid="ph-listing-tags">
            <div className="flex flex-wrap gap-1">{(d.tags || []).map((x) => <Badge key={x} variant="secondary" className="rounded-sm text-[10px]">{x}</Badge>)}</div>
          </ListingField>
          <div className="grid grid-cols-2 gap-3">
            <ListingField label={t("ph.draftProductType")} testid="ph-listing-3dartix-product-type">
              <div className="text-xs">{d.product_type || "—"}</div>
            </ListingField>
            <ListingField label={t("ph.listingType")} testid="ph-listing-3dartix-listing-type">
              <div className="text-xs">{d.listing_type || "—"}</div>
            </ListingField>
            <ListingField label={t("ph.draftCategoryId")} testid="ph-listing-3dartix-category">
              <div className="text-xs">{d.category_id ?? "—"}</div>
            </ListingField>
            <ListingField label={t("ph.draftSku")} testid="ph-listing-3dartix-sku">
              <div className="text-xs font-mono">{d.sku || "—"}</div>
            </ListingField>
            <ListingField label={t("ph.draftPrice")} testid="ph-listing-3dartix-price">
              <div className="text-xs">
                {d.is_free_product ? t("ph.draftFreeProduct")
                  : (d.price != null ? `${d.price} ${d.currency || ""}` : "—")}
              </div>
            </ListingField>
            <ListingField label={t("ph.draftStock")} testid="ph-listing-3dartix-stock">
              <div className="text-xs">{d.stock ?? "—"}</div>
            </ListingField>
          </div>
          {d.custom_fields && (
            <ListingField label={t("ph.draftCustomFields")} testid="ph-listing-3dartix-custom-fields">
              <pre className="text-[11px] font-mono whitespace-pre-wrap text-muted-foreground">{JSON.stringify(d.custom_fields, null, 2)}</pre>
            </ListingField>
          )}
          <ListingField label={t("ph.threedartixContractStatus")} testid="ph-listing-3dartix-contract-status">
            <Badge variant="secondary" className="rounded-sm text-[10px] gap-1 text-primary">
              <ShieldCheck size={11} />{t("ph.threedartixContractValid")}
            </Badge>
          </ListingField>
          {/* NO EtsyDraftPanel / DeliveryPanel / TranslationsPanel here — those are Etsy-only.
              31/10/2026: the 3DArtiX WRITE lives in its own panel, behind the per-asset human
              resale review (eligibility) — see backend app/product_hunter/threedartix_publish.py. */}
          <ThreeDArtixPublishPanel productId={productId} t={t} ownProduct={!!data.listing?.own_product} />
        </>
      ) : (
        <>
          <ListingField label={t("ph.draftTitleField")} testid="ph-listing-title"><div className="font-medium">{d.title || "—"}</div></ListingField>
          <ListingField label={t("ph.draftDescription")} testid="ph-listing-description"><p className="text-xs text-muted-foreground whitespace-pre-wrap">{d.description || "—"}</p></ListingField>
          <ListingField label={t("ph.draftTags")} testid="ph-listing-tags">
            <div className="flex flex-wrap gap-1">{(d.tags || []).map((x) => <Badge key={x} variant="secondary" className="rounded-sm text-[10px]">{x}</Badge>)}</div>
          </ListingField>
          <ListingField label={t("ph.draftKeywords")} testid="ph-listing-keywords">
            <div className="flex flex-wrap gap-1">{(d.keywords || []).map((x) => <Badge key={x} variant="outline" className="rounded-sm text-[10px]">{x}</Badge>)}</div>
          </ListingField>
          <ListingField label={t("ph.draftCategory")} testid="ph-listing-category"><div className="text-xs">{d.category || "—"}</div></ListingField>
          <ListingField label={t("ph.draftAttributes")} testid="ph-listing-attributes">
            <div className="grid grid-cols-2 gap-1 text-xs">
              {(d.attributes || []).map((a) => <div key={a.name} className="flex justify-between"><span className="text-muted-foreground">{a.name}</span><span>{a.value}</span></div>)}
            </div>
          </ListingField>
          <ListingField label={t("ph.draftAltTexts")} testid="ph-listing-alttexts">
            <ul className="text-xs text-muted-foreground list-disc pl-4">{(d.alt_texts || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
          </ListingField>
          <ListingField label={t("ph.draftPricing")} testid="ph-listing-pricing">
            {d.pricing ? (
              <div className="text-xs">
                <span className="font-medium">{d.pricing.suggested_min} – {d.pricing.suggested_max} {d.pricing.currency}</span>
                <p className="text-muted-foreground mt-1">{d.pricing.rationale}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{t("ph.draftPricingNonBinding")}</p>
              </div>
            ) : <div className="text-xs text-muted-foreground">{t("ph.draftPricingNone")}</div>}
          </ListingField>
          {d.evidence_notes && <ListingField label={t("ph.draftEvidenceNotes")} testid="ph-listing-evidence"><p className="text-[11px] text-muted-foreground">{d.evidence_notes}</p></ListingField>}

          <TranslationsPanel productId={productId} master={d} t={t} />

          <DeliveryPanel existing={data.delivery} t={t} productId={productId} onPrepareDelivery={onPrepareDelivery} />

          <EtsyDraftPanel d={d} listing={listing} existing={data.etsy_draft} t={t}
            productId={productId} onCreateDraft={onCreateDraft} />
        </>
      )}
    </div>
  );
}

// SPRINT-PH-21 F3 — "Multilingual versions" panel. It ONLY reads/writes through the existing F1/F2
// endpoints (/etsy-languages, /products/{id}/translations…): no generation logic and no prompt lives
// in the frontend. The Italian MASTER listing rendered above is never modified and NOTHING is sent
// to Etsy from here.
const TR_API = (id) => `/product-hunter/products/${id}/translations`;
const I18N_ERRORS = {
  chatgpt_browser_unavailable: "ph.i18n.errProviderUnavailable",
  etsy_llm_provider_unavailable: "ph.i18n.errProviderUnavailable",
  master_not_ready: "ph.i18n.errMasterNotReady",
  provider_invalid_json: "ph.i18n.errInvalidJson",
  provider_call_failed: "ph.i18n.errProviderCall",
  no_etsy_listing: "ph.i18n.errNoListing",
  etsy_not_connected: "ph.i18n.errNotConnected",
  listings_w_required: "ph.i18n.errListingsW",
  language_not_enabled_on_shop: "ph.i18n.errNotEnabled",
  primary_language_not_translatable: "ph.i18n.errPrimaryLang",
  translation_stale: "ph.i18n.errStalePush",
  etsy_write_not_verified: "ph.i18n.errNotVerified",
  no_translation: "ph.i18n.errNoTranslation",
  not_ready: "ph.i18n.errNotReady",
};

function I18nStatusBadge({ code, status, stale, t }) {
  if (status === "ready") {
    return (
      <span className="flex items-center gap-1">
        <Badge variant="secondary" className="rounded-sm text-[10px] gap-1 text-primary" data-testid={`ph-i18n-status-${code}`}>
          <CheckCircle size={10} weight="fill" />{t("ph.i18n.statusReady")}
        </Badge>
        {stale && (
          <Badge variant="outline" className="rounded-sm text-[10px] border-amber-500/50 text-amber-600"
            title={t("ph.i18n.staleHint")} data-testid={`ph-i18n-stale-${code}`}>
            {t("ph.i18n.stale")}
          </Badge>
        )}
      </span>
    );
  }
  if (status === "processing" || status === "pending") {
    return (
      <Badge variant="outline" className="rounded-sm text-[10px] gap-1" data-testid={`ph-i18n-status-${code}`}>
        <Spinner size={10} className="animate-spin" />
        {t(status === "processing" ? "ph.i18n.statusProcessing" : "ph.i18n.statusPending")}
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge variant="outline" className="rounded-sm text-[10px] text-destructive border-destructive/40"
        data-testid={`ph-i18n-status-${code}`}>
        {t("ph.i18n.statusFailed")}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="rounded-sm text-[10px] text-muted-foreground"
      data-testid={`ph-i18n-status-${code}`}>
      {t("ph.i18n.statusNone")}
    </Badge>
  );
}

function CompareRow({ label, children }) {
  return (
    <div className="border-t border-border pt-2">
      <div className="overline text-[9px] text-muted-foreground mb-1">{label}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">{children}</div>
    </div>
  );
}

function TagChips({ items, variant = "secondary", testid }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid={testid}>
      {(items || []).length
        ? items.map((x) => <Badge key={x} variant={variant} className="rounded-sm text-[10px]">{x}</Badge>)
        : <span className="text-muted-foreground">—</span>}
    </div>
  );
}

function TranslationsPanel({ productId, master, t }) {
  const [open, setOpen] = useState(true);
  const [cat, setCat] = useState(null);
  const [state, setState] = useState(null);
  const [docs, setDocs] = useState({});
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const poll = useRef(null);
  const selRef = useRef(null);

  const errText = (d) => {
    const key = I18N_ERRORS[d?.error];
    const base = key ? t(key) : t("ph.i18n.errGeneric");
    return d?.error_detail ? `${base} — ${String(d.error_detail).slice(0, 300)}` : base;
  };

  const openLang = async (code, force = false) => {
    setSel(code);
    selRef.current = code;
    if (docs[code] && !force) return;
    try {
      const { data } = await api.get(`${TR_API(productId)}/${code}`);
      setDocs((prev) => ({ ...prev, [code]: data }));
    } catch {
      setDocs((prev) => ({ ...prev, [code]: null }));
    }
  };

  const startPoll = () => {
    if (poll.current) clearInterval(poll.current);
    poll.current = setInterval(async () => {
      try {
        const { data } = await api.get(TR_API(productId));
        setState(data);
        if (data?.job?.status !== "running") {
          clearInterval(poll.current);
          poll.current = null;
          setBusy(false);
          setDocs({});
          if (selRef.current) openLang(selRef.current, true);
        }
      } catch { /* keep polling — the job row is the source of truth */ }
    }, 2000);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get("/product-hunter/etsy-languages"),
          api.get(TR_API(productId)),
        ]);
        if (!alive) return;
        setCat(c.data);
        setState(s.data);
        if (s.data?.job?.status === "running") { setBusy(true); startPoll(); }
      } catch {
        if (alive) toast.error(t("ph.i18n.loadError"));
      }
    })();
    return () => { alive = false; if (poll.current) clearInterval(poll.current); };
  }, [productId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (url, startedMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(url, {});
      if (data.status === "running") { toast.success(startedMsg); startPoll(); return; }
      if (data.status === "completed") { toast.info(t("ph.i18n.allUpToDate")); }
      else { toast.error(errText(data)); }
      const { data: fresh } = await api.get(TR_API(productId));
      setState(fresh);
    } catch {
      toast.error(t("ph.i18n.startError"));
    }
    setBusy(false);
  };

  // F4 — sends ALREADY GENERATED languages to the SAME Etsy listing (no new listing, no publish).
  const push = async (codes) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/product-hunter/products/${productId}/push-translations`,
                                      codes ? { languages: codes } : {});
      if (data.error) toast.error(errText(data));
      else {
        const pushed = (data.results || []).filter((r) => r.status === "pushed");
        const bad = (data.results || []).filter((r) => r.status === "failed" || r.status === "blocked");
        if (pushed.length) toast.success(t("ph.i18n.pushDone", { count: pushed.length }));
        bad.forEach((r) => toast.error(`${labels[r.language] || r.language}: ${errText(r)}`));
        if (!pushed.length && !bad.length) toast.info(t("ph.i18n.pushAlreadyDone"));
      }
      const { data: fresh } = await api.get(TR_API(productId));
      setState(fresh);
    } catch {
      toast.error(t("ph.i18n.pushError"));
    }
    setBusy(false);
  };

  const labels = Object.fromEntries((cat?.supported || []).map((x) => [x.code, x.label]));
  const enabledOnShop = Object.fromEntries((cat?.supported || []).map((x) => [x.code, x.enabled_on_shop]));
  const rows = Object.fromEntries((state?.languages || []).map((l) => [l.language, l]));
  const targets = cat?.selected_targets || [];
  const codes = [...targets, ...Object.keys(rows).filter((c) => !targets.includes(c))];
  const job = state?.job;
  const running = job?.status === "running";
  const prog = job?.progress || null;
  const doc = sel ? docs[sel] : null;
  const readyCount = Object.values(rows).filter((r) => r.status === "ready").length;

  return (
    <div className="border border-border rounded-sm bg-muted/20" data-testid="ph-i18n-section">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 p-3 text-left" data-testid="ph-i18n-toggle">
        <Translate size={15} className="text-primary shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium">{t("ph.i18n.title")}</div>
          <div className="text-[11px] text-muted-foreground">{t("ph.i18n.hint")}</div>
        </div>
        <CaretDown size={14} className={`ml-auto shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-muted-foreground">{t("ph.i18n.targetsLabel")}:</span>
            <span className="flex flex-wrap gap-1" data-testid="ph-i18n-targets">
              {targets.length
                ? targets.map((c) => <Badge key={c} variant="outline" className="rounded-sm text-[10px]">{labels[c] || c}</Badge>)
                : <span className="text-[11px] text-muted-foreground">{t("ph.i18n.noTargets")}</span>}
            </span>
            <Button size="sm" className="rounded-sm h-8 text-xs ml-auto" onClick={() =>
              run(`/product-hunter/products/${productId}/generate-translations`, t("ph.i18n.started"))}
              disabled={busy || running || !targets.length} data-testid="ph-i18n-generate-all">
              {busy || running
                ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.i18n.generating")}</>
                : <><Sparkle size={13} className="mr-1" />{t("ph.i18n.generateAll")}</>}
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs"
              onClick={() => push(null)} disabled={busy || running || !readyCount}
              data-testid="ph-i18n-push-all">
              <Storefront size={13} className="mr-1" />{t("ph.i18n.publishAll")}
            </Button>
          </div>

          {cat?.shop && (
            <div className="text-[10px] text-muted-foreground" data-testid="ph-i18n-shop-state">
              {cat.shop.detection === "etsy_api"
                ? `${t("ph.i18n.shopEnabled")}: ${(cat.shop.enabled_languages || []).join(", ") || "—"}`
                : t(cat.shop.detection === "not_connected" ? "ph.i18n.shopNotConnected" : "ph.i18n.shopUnknown")}
            </div>
          )}

          {running && prog && (
            <div data-testid="ph-i18n-progress">
              <Progress value={prog.total ? (prog.done / prog.total) * 100 : 0} className="h-1.5" />
              <div className="text-[10px] text-muted-foreground mt-1">
                {t("ph.i18n.progress", { done: prog.done, total: prog.total })}
                {prog.current ? ` · ${t("ph.i18n.current", { lang: labels[prog.current] || prog.current })}` : ""}
                {` · ${t("ph.i18n.sequentialNote")}`}
              </div>
            </div>
          )}
          {job?.provider_error && !running && (
            <div className="text-[11px] text-destructive" data-testid="ph-i18n-job-error">
              {errText({ error: job.provider_error })}
            </div>
          )}

          <div className="space-y-1">
            {codes.map((code) => {
              const r = rows[code] || {};
              return (
                <div key={code} className="flex flex-wrap items-center gap-2 border border-border rounded-sm px-2 py-1.5 bg-background">
                  <button type="button" onClick={() => openLang(code)}
                    className={`text-xs font-medium ${sel === code ? "text-primary" : ""}`}
                    data-testid={`ph-i18n-tab-${code}`}>
                    {labels[code] || code}
                  </button>
                  <I18nStatusBadge code={code} status={r.status} stale={r.stale} t={t} />
                  {r.etsy_push?.pushed_at && r.etsy_push?.verified && (
                    <Badge variant="secondary" className="rounded-sm text-[10px] gap-1"
                      title={`${t("ph.i18n.pushedAt")}: ${String(r.etsy_push.pushed_at).slice(0, 16).replace("T", " ")}`}
                      data-testid={`ph-i18n-pushed-${code}`}>
                      <Storefront size={10} />{t("ph.i18n.onEtsy")}
                    </Badge>
                  )}
                  {enabledOnShop[code] === false && (
                    <Badge variant="outline" className="rounded-sm text-[10px] text-muted-foreground"
                      title={t("ph.i18n.errNotEnabled")} data-testid={`ph-i18n-notenabled-${code}`}>
                      {t("ph.i18n.notEnabled")}
                    </Badge>
                  )}
                  {r.status === "ready" && (
                    <span className="text-[10px] text-muted-foreground">
                      {t("ph.i18n.confidence")}: {r.confidence ?? 0} · {r.provider || "default"}
                    </span>
                  )}
                  {r.error && (
                    <span className="text-[10px] text-destructive truncate max-w-[45%]"
                      title={r.error_detail || r.error} data-testid={`ph-i18n-error-${code}`}>
                      {r.error}
                    </span>
                  )}
                  {!r.error && r.etsy_push?.error && (
                    <span className="text-[10px] text-destructive truncate max-w-[45%]"
                      title={r.etsy_push.error_detail || r.etsy_push.error}
                      data-testid={`ph-i18n-push-error-${code}`}>
                      {r.etsy_push.error}
                    </span>
                  )}
                  <Button size="sm" variant="outline" className="rounded-sm h-6 text-[11px] ml-auto"
                    onClick={() => run(`${TR_API(productId)}/${code}/retry`, t("ph.i18n.retryStarted"))}
                    disabled={busy || running} data-testid={`ph-i18n-retry-${code}`}>
                    <ArrowsClockwise size={11} className="mr-1" />
                    {r.status === "ready" ? t("ph.i18n.regenerate") : t("ph.i18n.generate")}
                  </Button>
                  {r.etsy_push?.pushed_at && r.etsy_push?.verified
                    ? <Button size="sm" variant="outline" className="rounded-sm h-6 text-[11px]"
                        onClick={() => push([code])} disabled={busy || running}
                        data-testid={`ph-i18n-push-${code}`}>
                        <Storefront size={11} className="mr-1" />{t("ph.i18n.republish")}
                      </Button>
                    : null}
                  {r.status === "ready" && !(r.etsy_push?.pushed_at && r.etsy_push?.verified) && (
                    <Button size="sm" variant="outline" className="rounded-sm h-6 text-[11px]"
                      onClick={() => push([code])} disabled={busy || running || enabledOnShop[code] === false}
                      title={enabledOnShop[code] === false ? t("ph.i18n.errNotEnabled") : undefined}
                      data-testid={`ph-i18n-push-${code}`}>
                      <Storefront size={11} className="mr-1" />{t("ph.i18n.publish")}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="border border-border rounded-sm p-2 bg-background" data-testid="ph-i18n-compare">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px] font-medium">
              <div className="flex items-center gap-1" data-testid="ph-i18n-tab-master">
                <ShieldCheck size={11} className="text-primary" />{t("ph.i18n.compareMaster")}
              </div>
              <div data-testid="ph-i18n-compare-lang">
                {sel ? (labels[sel] || sel) : t("ph.i18n.selectLang")}
              </div>
            </div>
            {!sel || !doc || doc.status !== "ready" ? (
              <div className="text-[11px] text-muted-foreground mt-2" data-testid="ph-i18n-compare-empty">
                {sel ? t("ph.i18n.notGeneratedYet") : t("ph.i18n.selectLangHint")}
              </div>
            ) : (
              <div className="space-y-2 mt-2">
                <CompareRow label={t("ph.draftTitleField")}>
                  <div className="font-medium" data-testid="ph-i18n-master-title">{master?.title || "—"}</div>
                  <div className="font-medium" data-testid={`ph-i18n-lang-title-${sel}`}>{doc.draft?.title || "—"}</div>
                </CompareRow>
                <CompareRow label={t("ph.draftDescription")}>
                  <p className="text-muted-foreground whitespace-pre-wrap max-h-40 overflow-auto">{master?.description || "—"}</p>
                  <p className="text-muted-foreground whitespace-pre-wrap max-h-40 overflow-auto"
                    data-testid={`ph-i18n-lang-description-${sel}`}>{doc.draft?.description || "—"}</p>
                </CompareRow>
                <CompareRow label={t("ph.draftTags")}>
                  <TagChips items={master?.tags} />
                  <TagChips items={doc.draft?.tags} testid={`ph-i18n-lang-tags-${sel}`} />
                </CompareRow>
                <CompareRow label={t("ph.draftKeywords")}>
                  <TagChips items={master?.keywords} variant="outline" />
                  <TagChips items={doc.draft?.keywords} variant="outline" testid={`ph-i18n-lang-keywords-${sel}`} />
                </CompareRow>
                <div className="border-t border-border pt-2 text-[10px] text-muted-foreground"
                  data-testid={`ph-i18n-lang-facts-${sel}`}>
                  {t("ph.i18n.factsInherited")}
                  {doc.generated_at ? ` · ${t("ph.i18n.generatedAt")}: ${String(doc.generated_at).slice(0, 16).replace("T", " ")}` : ""}
                </div>
              </div>
            )}
          </div>

          <p className="text-[10px] text-muted-foreground" data-testid="ph-i18n-master-note">
            {t("ph.i18n.masterNote")} {t("ph.i18n.pushNote")}
          </p>
        </div>
      )}
    </div>
  );
}

function DeliveryPanel({ existing, t, productId, onPrepareDelivery }) {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(existing || null);
  const ready = state?.status === "ready";
  const failed = state?.status === "failed";
  const noModel = state?.error === "no_acquired_model";
  const kb = (n) => (n ? `${(n / 1024).toFixed(1)} KB` : "—");

  const run = async () => {
    setBusy(true);
    try {
      const res = await onPrepareDelivery(productId);
      setState(res.status ? { status: res.status, error: res.error, retryable: res.retryable,
        zip: res.zip, download: res.download } : res);
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-border rounded-sm p-3" data-testid="ph-delivery-panel">
      <div className="flex items-center gap-2 mb-2">
        <Package size={15} className="text-primary" />
        <span className="text-sm font-medium">{t("ph.digitalDelivery")}</span>
        {ready && <Badge variant="outline" className="rounded-sm text-[10px] text-primary border-primary/40" data-testid="ph-delivery-ready">{t("ph.readyForReview")}</Badge>}
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">{t("ph.digitalDeliveryNote")}</p>

      {ready ? (
        <div className="text-xs space-y-1" data-testid="ph-delivery-ready-info">
          <div className="flex justify-between"><span className="text-muted-foreground">{t("ph.deliveryZip")}</span><span className="font-mono">{state.zip?.filename}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{t("ph.deliverySize")}</span><span>{kb(state.zip?.size)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{t("ph.deliverySourceModel")}</span><span className="font-mono truncate max-w-[55%]" title={state.download?.model_filename}>{state.download?.model_filename || "—"}</span></div>
          <div className="flex justify-between items-center"><span className="text-muted-foreground">{t("ph.deliveryUrlStatus")}</span>
            <Badge variant="outline" className="rounded-sm text-[10px] text-primary border-primary/40"><ShieldCheck size={10} className="mr-1" />{t("ph.reachable")}</Badge></div>
          {state.download?.url && <a href={state.download.url} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 pt-1" data-testid="ph-delivery-url"><ArrowSquareOut size={11} />{t("ph.testDownload")}</a>}
        </div>
      ) : (
        <div className="space-y-2">
          {noModel
            ? <p className="text-[11px] text-amber-600" data-testid="ph-delivery-no-model">{t("ph.deliveryNoModelMsg")}</p>
            : failed && <p className="text-[11px] text-destructive" data-testid="ph-delivery-failed">{t("ph.deliveryFailedMsg")}</p>}
          <Button size="sm" variant="secondary" className="rounded-sm h-8 text-xs" onClick={run}
            disabled={busy || noModel} data-testid="ph-prepare-delivery-btn">
            {busy ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.packaging")}</>
              : <><Package size={13} className="mr-1" />{failed ? t("ph.retry") : t("ph.prepareDelivery")}</>}
          </Button>
        </div>
      )}
    </div>
  );
}

function EtsyCategoryPicker({ value, onPick, t }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [state, setState] = useState(null);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    let alive = true;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/product-hunter/etsy-categories",
                                       { params: { q, limit: 60 } });
        if (!alive) return;
        setRows(data.categories || []);
        setState(data);
      } catch {
        if (alive) setState({ status: "failed", error: "taxonomy_unavailable" });
      }
    }, q ? 400 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [q]);

  return (
    <div className="space-y-1 text-xs col-span-2" data-testid="ph-etsy-category-picker">
      <span className="text-muted-foreground">{t("ph.etsyCategory")}</span>
      <Input className="h-8 rounded-sm" placeholder={t("ph.etsyCategorySearch")} value={q}
        onChange={(e) => setQ(e.target.value)} data-testid="ph-etsy-category-search" />
      <select className="w-full h-8 rounded-sm border border-border bg-background px-2"
        value={value || ""} data-testid="ph-etsy-category-select"
        onChange={(e) => {
          const row = rows.find((x) => String(x.id) === e.target.value);
          setPicked(row || null);
          onPick(e.target.value);
        }}>
        <option value="">{t("ph.etsyCategoryPick")}</option>
        {rows.map((c) => <option key={c.id} value={c.id}>{c.path} · {c.id}</option>)}
      </select>
      {picked && (
        <p className="text-[10px] text-primary" data-testid="ph-etsy-category-picked">
          {picked.path} · taxonomy_id {picked.id}
        </p>
      )}
      {state && state.status !== "ok" && (
        <p className="text-[10px] text-amber-600" data-testid="ph-etsy-category-error">
          {state.error === "etsy_not_connected" ? t("ph.etsyCategoryNotConnected")
            : t("ph.etsyCategoryError")}
        </p>
      )}
      {state?.status === "ok" && !rows.length && (
        <p className="text-[10px] text-muted-foreground" data-testid="ph-etsy-category-empty">
          {t("ph.etsyCategoryNone")}
        </p>
      )}
    </div>
  );
}

function EtsyDraftPanel({ d, listing, existing, t, productId, onCreateDraft }) {
  const [opts, setOpts] = useState({ who_made: "i_did", when_made: "2020_2026",
    type: "download", quantity: 999, price: "", taxonomy_id: d?.taxonomy_id ? String(d.taxonomy_id) : "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(existing ? { status: "created", listing_id: existing.listing_id,
    state: existing.state, url: existing.url, image_ids: existing.image_ids || [] } : null);
  const needsPrice = !d?.pricing;
  const [needTax, setNeedTax] = useState(false);
  const created = result?.status === "created"
    ? result
    : (existing ? { listing_id: existing.listing_id, url: existing.url,
                    image_ids: existing.image_ids || [] } : null);
  const authReq = result?.status === "authorization_required";

  const submit = async () => {
    setBusy(true);
    const payload = { who_made: opts.who_made, when_made: opts.when_made, type: opts.type,
      quantity: Number(opts.quantity) || 999 };
    if (needsPrice && opts.price) payload.price = Number(opts.price);
    if (opts.taxonomy_id) payload.taxonomy_id = Number(opts.taxonomy_id);
    // A draft already exists → the user is publishing again: ask for an ADDITIONAL draft.
    if (created) payload.create_new = true;
    try {
      const res = await onCreateDraft(productId, payload);
      setResult(res);
      if (res.error === "taxonomy_required") setNeedTax(true);
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-primary/30 rounded-sm p-3 bg-primary/5" data-testid="ph-etsy-draft-panel">
      <div className="flex items-center gap-2 mb-2">
        <Storefront size={15} className="text-primary" />
        <span className="text-sm font-medium">{t("ph.createEtsyDraftTitle")}</span>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">{t("ph.createEtsyDraftNote")}</p>

      {created && (
        <div className="text-xs space-y-1 mb-3 pb-3 border-b border-primary/20" data-testid="ph-etsy-draft-created">
          <div className="flex items-center gap-2 text-primary font-medium">
            <CheckCircle size={14} weight="fill" />{t("ph.etsyDraftCreatedState")}
            <Badge variant="outline" className="rounded-sm text-[10px]">state: {result?.state || existing?.state}</Badge>
          </div>
          <div>{t("ph.etsyListingRef")}: <span className="font-mono">{created.listing_id}</span></div>
          <div>{(created.image_ids || []).length} {t("ph.assetsAcquired")} {t("ph.uploaded")}</div>
          {created.url && <a href={created.url} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1" data-testid="ph-etsy-draft-link"><ArrowSquareOut size={11} />{t("ph.viewEtsyDraft")}</a>}
          <p className="text-[10px] text-muted-foreground pt-1">{t("ph.etsyNoPublish")}</p>
        </div>
      )}

      <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="space-y-1"><span className="text-muted-foreground">{t("ph.whoMade")}</span>
              <select className="w-full h-8 rounded-sm border border-border bg-background px-2" data-testid="ph-etsy-whomade"
                value={opts.who_made} onChange={(e) => setOpts({ ...opts, who_made: e.target.value })}>
                <option value="i_did">i_did</option><option value="someone_else">someone_else</option><option value="collective">collective</option>
              </select></label>
            <label className="space-y-1"><span className="text-muted-foreground">{t("ph.whenMade")}</span>
              <select className="w-full h-8 rounded-sm border border-border bg-background px-2" data-testid="ph-etsy-whenmade"
                value={opts.when_made} onChange={(e) => setOpts({ ...opts, when_made: e.target.value })}>
                <option value="2020_2026">2020_2026</option><option value="2010_2019">2010_2019</option><option value="made_to_order">made_to_order</option>
              </select></label>
            <label className="space-y-1"><span className="text-muted-foreground">{t("ph.listingType")}</span>
              <select className="w-full h-8 rounded-sm border border-border bg-background px-2" data-testid="ph-etsy-type"
                value={opts.type} onChange={(e) => setOpts({ ...opts, type: e.target.value })}>
                <option value="download">download</option><option value="physical">physical</option>
              </select></label>
            <label className="space-y-1"><span className="text-muted-foreground">{t("ph.quantity")}</span>
              <Input type="number" min="1" className="h-8 rounded-sm" data-testid="ph-etsy-quantity"
                value={opts.quantity} onChange={(e) => setOpts({ ...opts, quantity: e.target.value })} /></label>
            {needsPrice && (
              <label className="space-y-1"><span className="text-muted-foreground">{t("ph.priceRequired")} ({listing?.currency || "EUR"})</span>
                <Input type="number" step="0.01" min="0" className="h-8 rounded-sm" data-testid="ph-etsy-price"
                  value={opts.price} onChange={(e) => setOpts({ ...opts, price: e.target.value })} /></label>
            )}
            {needTax && (
              <label className="space-y-1"><span className="text-muted-foreground">{t("ph.taxonomyId")}</span>
                <Input type="number" min="1" className="h-8 rounded-sm" data-testid="ph-etsy-taxonomy"
                  value={opts.taxonomy_id} onChange={(e) => setOpts({ ...opts, taxonomy_id: e.target.value })} /></label>
            )}
            <EtsyCategoryPicker value={opts.taxonomy_id} t={t}
              onPick={(id) => setOpts({ ...opts, taxonomy_id: id })} />
          </div>
          {authReq && <p className="text-[11px] text-amber-600" data-testid="ph-etsy-auth-required">{t("ph.etsyAuthRequiredMsg")}</p>}
          {needTax && <p className="text-[11px] text-amber-600" data-testid="ph-etsy-taxonomy-required">{t("ph.etsyTaxonomyRequiredMsg")}</p>}
          <Button size="sm" className="rounded-sm h-8 text-xs" onClick={submit} disabled={busy}
            data-testid="ph-create-etsy-draft-btn">
            {busy ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.creatingDraft")}</>
              : <><Storefront size={13} className="mr-1" />{created ? t("ph.createAnotherEtsyDraft") : t("ph.createEtsyDraftTitle")}</>}
          </Button>
        </div>
    </div>
  );
}

// 31/10/2026 — "Publish on 3DArtiX": per-asset human resale review + one real 3DArtiX product.
function ThreeDArtixPublishPanel({ productId, t, ownProduct = false }) {
  const [elig, setElig] = useState(null);
  const [pub, setPub] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [reason, setReason] = useState("");
  // Own products (the user is the author): publish visible by default (31/10/2026).
  const [status, setStatus] = useState(ownProduct ? "publish" : "draft");
  const [extId, setExtId] = useState("");
  const load = async () => {
    try {
      const [e, p] = await Promise.all([
        api.get(`/product-hunter/products/${productId}/eligibility`),
        api.get(`/product-hunter/products/${productId}/publish-3dartix`),
      ]);
      setElig(e.data); setPub(p.data?.publication || null);
    } catch (err) { setElig({ error: true }); }
  };
  useEffect(() => { if (productId) load(); }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps
  const errMsg = (err) => err?.response?.data?.detail || err?.message || "error";
  const review = async () => {
    setBusy(true);
    try {
      await api.post(`/product-hunter/products/${productId}/eligibility/review`,
        { decision_id: elig.id, decision: "eligible", reason });
      toast.success(t("ph.tdxReviewSaved")); setReason(""); setConfirm(false); await load();
    } catch (err) { toast.error(String(errMsg(err))); } finally { setBusy(false); }
  };
  const call = async (path, body, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/product-hunter/products/${productId}/${path}`, body || {});
      if (data.status === "created" || data.status === "ok") toast.success(okMsg);
      else if (data.status === "ambiguous") toast.warning(t("ph.tdxAmbiguous"));
      else if (data.status === "blocked") toast.error(t("ph.tdxNeedsReview"));
      else if (data.status === "failed") toast.error(`${data.error}${data.detail ? `: ${data.detail}` : ""}${data.errors ? `: ${data.errors.join("; ")}` : ""}`);
      await load();
    } catch (err) { toast.error(String(errMsg(err))); } finally { setBusy(false); }
  };
  if (!elig) return <div className="text-xs text-muted-foreground" data-testid="ph-tdx-publish-loading"><Spinner size={14} className="animate-spin inline mr-1" />{t("ph.loading")}</div>;
  const eligible = elig.status === "eligible";
  const media = pub?.media || {};
  return (
    <div className="border border-primary/40 rounded-sm p-3 space-y-3" data-testid="ph-tdx-publish-panel">
      <div className="flex items-center gap-2 font-medium"><Cube size={16} className="text-primary" />{t("ph.tdxPublishTitle")}</div>

      <div className="text-xs space-y-1" data-testid="ph-tdx-eligibility">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{t("ph.tdxRights")}:</span>
          <Badge variant={eligible ? "secondary" : "outline"} className={`rounded-sm text-[10px] ${eligible ? "text-primary" : elig.status === "blocked" ? "text-destructive" : "text-amber-600"}`}>
            {eligible ? t("ph.tdxEligible") : elig.status === "blocked" ? t("ph.tdxBlocked") : t("ph.tdxReviewRequired")}
          </Badge>
        </div>
        <div className="text-[11px] text-muted-foreground">{elig.reason}</div>
      </div>

      {!eligible && !pub?.external_id && elig.id && (
        <div className="space-y-2 border border-amber-500/40 bg-amber-500/5 rounded-sm p-2" data-testid="ph-tdx-review-form">
          <div className="text-[11px] text-amber-700">{t("ph.tdxReviewIntro")}</div>
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="ph-tdx-review-confirm" />
            <span>{t("ph.tdxReviewConfirm")}</span>
          </label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={t("ph.tdxReviewReasonPh")} className="text-xs" data-testid="ph-tdx-review-reason" />
          <Button size="sm" onClick={review} disabled={busy || !confirm || reason.trim().length < 5}
            data-testid="ph-tdx-review-save">{t("ph.tdxReviewSave")}</Button>
        </div>
      )}

      {eligible && (!pub || pub.status === "failed") && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="ph-tdx-publish-form">
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="rounded-sm border border-border bg-card px-2 py-1.5 text-xs" data-testid="ph-tdx-publish-status">
            <option value="draft">{t("ph.tdxAsDraft")}</option>
            <option value="publish">{t("ph.tdxAsPublished")}</option>
          </select>
          <Button size="sm" onClick={() => call("publish-3dartix", { status }, t("ph.tdxCreated"))} disabled={busy}
            data-testid="ph-tdx-publish-btn">{busy ? <Spinner size={14} className="animate-spin" /> : <Cube size={14} />}{t("ph.tdxPublishBtn")}</Button>
          {pub?.status === "failed" && <span className="text-[11px] text-destructive">{pub.error}</span>}
        </div>
      )}

      {pub && pub.status !== "failed" && (
        <div className="text-xs space-y-2" data-testid="ph-tdx-publication">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="rounded-sm text-[10px]">{pub.status}</Badge>
            {pub.external_id && <span>ID 3DArtiX: <b>{pub.external_id}</b></span>}
            {pub.remote_status && <Badge variant="outline" className="rounded-sm text-[10px]">{pub.remote_status}</Badge>}
            {pub.url && <a href={pub.url} target="_blank" rel="noopener noreferrer" className="text-primary underline">{t("ph.tdxOpen")}</a>}
          </div>
          {pub.status === "created" && (
            <>
              <div className="text-muted-foreground">
                {t("ph.tdxImages")}: {(media.images_done || []).length}/{media.images_total ?? "?"} · {t("ph.tdxModel")}: {media.model_done ? `✓ ${media.model_file || ""}` : "—"}
              </div>
              {(media.errors || []).length > 0 && (
                <ul className="text-[11px] text-destructive list-disc pl-4">{media.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              )}
              {(pub.warnings || []).length > 0 && (
                <ul className="text-[11px] text-amber-700 list-disc pl-4">{pub.warnings.map((w, i) => <li key={i}>{typeof w === "string" ? w : JSON.stringify(w)}</li>)}</ul>
              )}
              <div className="flex gap-2 flex-wrap">
                {((media.errors || []).length > 0 || !media.model_done) && (
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => call("publish-3dartix/retry-media", {}, t("ph.tdxMediaRetried"))}
                    data-testid="ph-tdx-retry-media">{t("ph.tdxRetryMedia")}</Button>
                )}
                {pub.requested_status !== "publish" && (
                  <Button size="sm" disabled={busy} onClick={() => call("publish-3dartix/status", { status: "publish" }, t("ph.tdxPublishedNow"))}
                    data-testid="ph-tdx-publish-now">{t("ph.tdxPublishNow")}</Button>
                )}
              </div>
            </>
          )}
          {pub.status === "ambiguous" && (
            <div className="space-y-2 border border-amber-500/40 rounded-sm p-2" data-testid="ph-tdx-ambiguous">
              <div className="text-[11px] text-amber-700">{t("ph.tdxAmbiguousHelp")}</div>
              <div className="flex gap-2 flex-wrap items-center">
                <Input value={extId} onChange={(e) => setExtId(e.target.value)} placeholder="ID" className="w-28 h-8 text-xs" />
                <Button size="sm" variant="outline" disabled={busy || !extId.trim()} onClick={() => call("publish-3dartix/resolve", { external_id: extId.trim() }, t("ph.tdxResolved"))}>{t("ph.tdxResolveExists")}</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => call("publish-3dartix/resolve", { external_id: null }, t("ph.tdxResolved"))}>{t("ph.tdxResolveMissing")}</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ListingField({ label, children, testid }) {
  return (
    <div className="border border-border rounded-sm p-3" data-testid={testid}>
      <div className="overline text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  );
}

function ImgTile({ src, t }) {
  const [ok, setOk] = useState(true);
  if (!src || !ok) {
    return (
      <div className="w-full h-28 rounded-sm border border-dashed border-border bg-secondary/30 flex items-center justify-center">
        <ImageIcon size={22} className="text-muted-foreground/40" />
      </div>
    );
  }
  return <img src={src} alt="" className="w-full h-28 object-cover rounded-sm border border-border" onError={() => setOk(false)} />;
}


function DetailRow({ label, children, testid }) {
  return (
    <div className="border border-border rounded-sm p-3" data-testid={testid}>
      <div className="overline text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  );
}

function ModelAcquireRow({ c, t, model, onAcquireModel, acquired }) {
  const st = model?.status || c.model_status;
  const busy = st === "acquiring" || st === "queued" || st === "waiting_extension" || st === "uploading";
  const isAcquired = st === "acquired";
  const MAP = {
    acquired: { cls: "text-primary border-primary/40 bg-primary/5", label: t("ph.modelAcquiredBadge") },
    access_required: { cls: "text-amber-500 border-amber-500/40 bg-amber-500/5", label: t("ph.modelAccessRequiredBadge") },
    license_restricted: { cls: "text-amber-500 border-amber-500/40 bg-amber-500/5", label: t("ph.modelLicenseBadge") },
    license_required: { cls: "text-amber-500 border-amber-500/40 bg-amber-500/5", label: t("ph.modelLicenseRequiredBadge") },
    cloudflare_challenge: { cls: "text-amber-500 border-amber-500/40 bg-amber-500/5", label: t("ph.modelCloudflareBadge") },
    captcha_required: { cls: "text-amber-500 border-amber-500/40 bg-amber-500/5", label: t("ph.modelCaptchaRequiredBadge") },
    client_error: { cls: "text-destructive border-destructive/40", label: t("ph.modelClientErrorBadge") },
    blocked: { cls: "text-amber-500 border-amber-500/40 bg-amber-500/5", label: t("ph.modelBlockedBadge") },
    not_available: { cls: "text-muted-foreground border-border", label: t("ph.modelUnavailableBadge") },
    unavailable: { cls: "text-muted-foreground border-border", label: t("ph.modelUnavailableBadge") },
    failed: { cls: "text-destructive border-destructive/40", label: t("ph.failedShort") },
  };
  const badge = st && MAP[st];
  const ma = model?.model_acquisition || model;
  const lic = ma?.license_assessment;
  const retryable = !isAcquired && (st === "access_required" || st === "blocked" || st === "failed" ||
    st === "cloudflare_challenge" || st === "captcha_required" || st === "license_required" || st === "client_error");
  const busyLabel = st === "waiting_extension" ? t("ph.modelWaitingExtension")
    : st === "uploading" ? t("ph.modelUploading") : t("ph.acquiring");
  return (
    <div className="flex flex-col gap-1 mt-1 pt-2 border-t border-border/60" data-testid="ph-model-row">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("ph.modelLabel")}</span>
        {badge && <span className={`text-[10px] border rounded-sm px-1.5 py-0.5 ${badge.cls}`} data-testid="ph-model-status">{badge.label}</span>}
        <Button size="sm" variant="outline" className="rounded-sm h-6 text-[11px] ml-auto"
          onClick={onAcquireModel} disabled={busy || isAcquired} data-testid="ph-candidate-acquire-model">
          {busy ? <><Spinner size={11} className="animate-spin mr-1" />{busyLabel}</>
            : isAcquired ? t("ph.modelAcquiredBadge") : retryable ? t("ph.retry") : t("ph.acquireModel")}
        </Button>
      </div>
      {isAcquired && ma && (
        <div className="text-[10px] text-muted-foreground flex flex-wrap gap-x-3" data-testid="ph-model-evidence">
          {ma.file_type && <span>{ma.file_type}</span>}
          {ma.size != null && <span>{(ma.size / 1024).toFixed(1)} KB</span>}
          {lic && <span title={lic.notes}>{t("ph.license")}: {lic.license || "—"} · {t("ph.sellable")}: {lic.sellable ? t("ph.yes") : t("ph.no")} ({lic.confidence}%)</span>}
        </div>
      )}
      {!isAcquired && (model?.reason || model?.evidence) && (
        <div className="text-[10px] text-muted-foreground" data-testid="ph-model-reason">
          {model.reason ? model.reason.replace(/_/g, " ") : ""}{model.confidence != null ? ` · ${model.confidence}%` : ""}
        </div>
      )}
    </div>
  );
}

function EtsyPromptEditor({ t }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ prompt: "", default_prompt: "", is_custom: false });
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;
    api.get("/product-hunter/etsy-prompt").then((r) => {
      setState(r.data); setDraft(r.data.prompt || ""); setLoaded(true);
    }).catch(() => toast.error(t("ph.promptLoadError")));
  }, [open, loaded, t]);

  const save = async (value) => {
    setBusy(true);
    try {
      const { data } = await api.put("/product-hunter/etsy-prompt", { prompt: value });
      setState(data); setDraft(data.prompt || "");
      toast.success(value.trim() ? t("ph.promptSaved") : t("ph.promptRestored"));
    } catch {
      toast.error(t("ph.promptSaveError"));
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-5 border border-border rounded-sm bg-muted/30" data-testid="ph-etsy-prompt-section">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 p-3 text-left" data-testid="ph-etsy-prompt-toggle">
        <Sparkle size={15} className="text-primary shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium">{t("ph.promptTitle")}</div>
          <div className="text-[11px] text-muted-foreground">{t("ph.promptHint")}</div>
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {state.is_custom && (
            <Badge variant="outline" className="rounded-sm text-[10px]" data-testid="ph-etsy-prompt-custom-badge">
              {t("ph.promptCustomized")}
            </Badge>
          )}
          <CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={12}
            spellCheck={false}
            className="rounded-sm text-xs font-mono leading-relaxed"
            data-testid="ph-etsy-prompt-textarea" />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" className="rounded-sm h-8 text-xs" onClick={() => save(draft)}
              disabled={busy || !draft.trim()} data-testid="ph-etsy-prompt-save">
              {busy ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.promptSaving")}</>
                : t("ph.promptSave")}
            </Button>
            <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs"
              onClick={() => setDraft(state.default_prompt || "")} disabled={busy}
              data-testid="ph-etsy-prompt-load-default">
              {t("ph.promptLoadDefault")}
            </Button>
            {state.is_custom && (
              <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs"
                onClick={() => save("")} disabled={busy} data-testid="ph-etsy-prompt-reset">
                {t("ph.promptReset")}
              </Button>
            )}
            <span className="text-[11px] text-muted-foreground ml-auto" data-testid="ph-etsy-prompt-chars">
              {draft.length} / 30000
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground">{t("ph.promptNote")}</p>
        </div>
      )}
    </div>
  );
}

// 3DArtiX equivalent of EtsyPromptEditor — reads/writes the SEPARATE org-level `threedartix_prompt`
// (GET/PUT /product-hunter/threedartix-prompt), never `etsy_prompt`. UNLIKE EtsyPromptEditor there is
// NO built-in default (backend/app/product_hunter/etsy_prep.py `resolve_prompt()`): `configured=false`
// means "Prepare for 3DArtiX" will honestly fail with `prompt_not_configured` until the org sets one.
// This component NEVER reads or falls back to the Etsy prompt in any way — a genuinely empty/absent
// 3DArtiX prompt is shown as an explicit empty state, never silently backed by Etsy's value.
function ThreeDArtixPromptEditor({ t }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ prompt: null, default_prompt: null, is_custom: false, configured: false });
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;
    api.get("/product-hunter/threedartix-prompt").then((r) => {
      setState(r.data); setDraft(r.data.prompt || ""); setLoaded(true);
    }).catch(() => toast.error(t("ph.promptLoadError")));
  }, [open, loaded, t]);

  const save = async (value) => {
    setBusy(true);
    try {
      const { data } = await api.put("/product-hunter/threedartix-prompt", { prompt: value });
      setState(data); setDraft(data.prompt || "");
      toast.success(value.trim() ? t("ph.threedartixPromptSaved") : t("ph.threedartixPromptCleared"));
    } catch {
      toast.error(t("ph.promptSaveError"));
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-5 border border-border rounded-sm bg-muted/30" data-testid="ph-3dartix-prompt-section">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 p-3 text-left" data-testid="ph-3dartix-prompt-toggle">
        <Sparkle size={15} className="text-primary shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium">{t("ph.threedartixPromptTitle")}</div>
          <div className="text-[11px] text-muted-foreground">{t("ph.threedartixPromptHint")}</div>
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {loaded && (state.configured ? (
            <Badge variant="secondary" className="rounded-sm text-[10px] text-primary" data-testid="ph-3dartix-prompt-configured-badge">
              {t("ph.threedartixPromptConfiguredBadge")}
            </Badge>
          ) : (
            <Badge variant="outline" className="rounded-sm text-[10px] text-amber-600 border-amber-500/50" data-testid="ph-3dartix-prompt-notconfigured-badge">
              {t("ph.threedartixPromptNotConfiguredBadge")}
            </Badge>
          ))}
          <CaretDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {loaded && !state.configured && !draft.trim() && (
            <p className="text-[11px] text-amber-600" data-testid="ph-3dartix-prompt-empty-hint">
              {t("ph.threedartixPromptEmptyHint")}
            </p>
          )}
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={12}
            spellCheck={false}
            className="rounded-sm text-xs font-mono leading-relaxed"
            data-testid="ph-3dartix-prompt-textarea" />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" className="rounded-sm h-8 text-xs" onClick={() => save(draft)}
              disabled={busy || !draft.trim()} data-testid="ph-3dartix-prompt-save">
              {busy ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.promptSaving")}</>
                : t("ph.promptSave")}
            </Button>
            {state.configured && (
              <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs"
                onClick={() => save("")} disabled={busy} data-testid="ph-3dartix-prompt-clear">
                {t("ph.threedartixPromptClear")}
              </Button>
            )}
            <span className="text-[11px] text-muted-foreground ml-auto" data-testid="ph-3dartix-prompt-chars">
              {draft.length} / 30000
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground">{t("ph.promptNote")}</p>
        </div>
      )}
    </div>
  );
}

function CandidateCard({ c, t, onInspect, onSave, onAcquire, acq, onAcquireModel, model, onRemove, removing }) {
  const scoreClass = c.score >= 75 ? "text-primary" : c.score >= 55 ? "text-amber-500" : "text-muted-foreground";
  const dup = c.duplicate_status && c.duplicate_status !== "unique";
  const [imgOk, setImgOk] = useState(true);
  const processing = acq?.status === "processing";
  // Effective outcome: prefer the LIVE acquire result; else the persisted acquisition_status.
  const live = acq && acq.status !== "processing" ? acq.status : null;
  const eff = live || c.acquisition_status || (c.status === "acquired" ? "acquired" : null);
  const isPartial = eff === "partial";
  const isCompleted = eff === "completed" || eff === "acquired";
  const isBlocked = eff === "blocked";
  const isFailed = eff === "failed" || eff === "error";
  const acqBadge = (isCompleted || isPartial)
    ? { cls: "text-primary border-primary/40 bg-primary/5", label: isPartial ? t("ph.partial") : t("ph.acquired") }
    : isBlocked ? { cls: "text-amber-500 border-amber-500/40 bg-amber-500/5", label: t("ph.blocked") }
    : isFailed ? { cls: "text-destructive border-destructive/40 bg-destructive/5", label: t("ph.failedShort") }
    : null;
  const acqDisabled = processing || isCompleted || isPartial || isBlocked; // failed → retryable
  const acqLabel = processing ? t("ph.acquiring") : (isCompleted || isPartial) ? t("ph.acquired") : t("ph.acquire");
  return (
    <div className="border border-border rounded-sm overflow-hidden flex flex-col" data-testid={`ph-candidate-${c.id}`}>
      <div className="h-36 bg-secondary/40 flex items-center justify-center overflow-hidden">
        {c.preview_images?.[0] && imgOk
          ? <img src={c.preview_images[0]} alt="" className="w-full h-full object-cover" data-testid="ph-candidate-preview" onError={() => setImgOk(false)} />
          : <ImageIcon size={28} className="text-muted-foreground/40" />}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm font-medium line-clamp-2" data-testid="ph-candidate-title">{c.title || t("ph.untitled")}</div>
          <div className={`font-heading text-lg font-bold shrink-0 ${scoreClass}`} data-testid="ph-candidate-score">{Math.round(c.score)}</div>
        </div>
        <div className="text-[11px] text-muted-foreground">
          {c.source} · {c.creator || t("ph.unknownCreator")}
        </div>
        <div className="flex flex-wrap gap-1">
          {(c.labels || []).slice(0, 3).map((l) => (
            <Badge key={l} variant="outline" className="rounded-sm text-[10px]" data-testid={`ph-candidate-label-${l}`}>{t(`ph.label.${l}`, l)}</Badge>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-1 text-[11px] text-muted-foreground mt-1">
          <span data-testid="ph-candidate-model">{t("ph.model")}: {c.model_available === true ? t("ph.yes") : c.model_available === false ? t("ph.no") : t("ph.unknown")}</span>
          <span data-testid="ph-candidate-license">{t("ph.license")}: {c.license ? t("ph.available") : t("ph.unavailable")}</span>
          <span data-testid="ph-candidate-dup">{t("ph.duplicate")}: {t(`ph.dupStatus.${c.duplicate_status || "unique"}`)}</span>
          <span>{t("ph.access")}: {t("ph.publicDiscovery")}</span>
        </div>
        <div className="flex items-center gap-2 mt-auto pt-2">
          {c.url && <a href={c.url} target="_blank" rel="noreferrer" data-testid="ph-candidate-source-link"
            className="text-xs text-primary inline-flex items-center gap-1"><ArrowSquareOut size={12} />{t("ph.viewSource")}</a>}
          {acqBadge && (
            <span className={`text-[10px] border rounded-sm px-1.5 py-0.5 ${acqBadge.cls}`} data-testid="ph-candidate-acq-status">
              {acqBadge.label}{(isCompleted || isPartial) && acq?.count != null ? ` · ${acq.count} ${t("ph.assetsAcquired")}` : ""}
            </span>
          )}
          <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs ml-auto" onClick={onInspect} data-testid="ph-candidate-inspect">{t("ph.inspect")}</Button>          <Button size="sm" variant="secondary" className="rounded-sm h-7 text-xs" onClick={onSave} disabled={c.status === "saved_for_acquisition" || isCompleted || isPartial || isBlocked} data-testid="ph-candidate-save">
            {c.status === "saved_for_acquisition" ? t("ph.saved") : t("ph.saveForAcq")}
          </Button>
          <Button size="sm" className="rounded-sm h-7 text-xs" onClick={onAcquire} disabled={acqDisabled} data-testid="ph-candidate-acquire">
            {processing ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.acquiring")}</> : acqLabel}
          </Button>
        </div>
        <ModelAcquireRow c={c} t={t} model={model} onAcquireModel={onAcquireModel} acquired={isCompleted || isPartial} />
        <Button size="sm" variant="outline"
          className="rounded-sm h-7 text-xs w-full text-destructive border-destructive/40 hover:bg-destructive/10"
          onClick={onRemove} disabled={removing} data-testid="ph-candidate-remove">
          {removing ? <><Spinner size={12} className="animate-spin mr-1" />{t("ph.removing")}</>
            : <><Trash size={13} className="mr-1" />{t("ph.removeProduct")}</>}
        </Button>
      </div>
    </div>
  );
}
